'use client';

import React, { forwardRef, useEffect, useId, useRef, useState } from 'react';
import '@/styles/search.css';

import { LocaleDetails } from 'shared/src/types/languages.type';
import { BudgetSpec, CategoryMap, PriceRange, PriceRangeSource, SearchBrief } from 'shared/src/types/categories.type';
import { AlgoliaHit, SearchIntent, SearchType } from 'shared/src/types/search.type';
import { KitSeedProduct } from 'shared/src/types/kit.type';
import { isAutoRedirectCandidate, passesDisplayGate, passesNearMissRescue, passesShelfFloor, SHELF_ABSOLUTE_FLOOR, SHELF_RELATIVE_RATIO } from 'shared/src/lib/search-gates';
import { isNearExactSubjectMatch } from 'shared/src/lib/fuzzy-subject-match';
import { resolveTurnLocale } from 'shared/src/lib/search-query-language';
import { parseBudgetFromText } from 'shared/src/utils/parse-budget';
import { getCurrencyForRegion } from 'shared/src/utils/currency';
import { stripSearchIntentQuery } from 'shared/src/server/search/search-intent-url';

import { startPageSearch } from '@/server/search/start-page-search';
import { createSearchMap } from '@/server/search/create-search-map';
import { startKitSearch } from '@/server/search/start-kit-search';
import { searchFocusShelf, searchPages, searchPagesWithStatus, type SearchPagesResult } from '@/server/search/get-algolia-search';
import { addStreamCard, streamShelfLookup } from '@/lib/shelf-stream';
import { findExistingProductPage } from '@/server/search/get-supabase-search';
import { closedFocusProducts, closedSuggestedAnswers, openingWordsForShelf, parseAssistantMessage, type SearchScope } from '@/lib/parse-assistant-message';
import { withBudgetQuery } from '@/lib/budget-query';
import { addToSearchHistory, getSearchHistory } from '@/lib/search-history';
import { isDeliberateRefusal, refusalRetryAfterSeconds } from '@/lib/search/deliberate-refusal';
import {
  onDegradedShelf,
  SHELF_LOOKUP_RETRY_DELAY_MS,
  type DegradedShelfAction,
} from '@/lib/search/shelf-degraded-policy';
import {
  boundedTurnFetch,
  isNetworkFailure,
  searchRetryPlan,
  NETWORK_RETRY_DELAY_MS,
  type FailedSearchStage,
} from '@/lib/search/search-retry';
import { pendingTurn, shopperTurns } from '@/lib/search/shelf-judge';
import { normalizeClassifiedIntent } from '@/lib/search/normalize-classified-intent';
import { createSearchHelperGate } from '@/lib/search/search-helper-gate';
import { track } from '@/lib/tracking/tracker';
import { getChatTranscript, type ChatTranscript } from '@/lib/chat-history';
import { clearSearchReturnStash } from '@/lib/search-return-stash';
import { useChatTranscriptPersistence } from '@/hooks/use-chat-transcript-persistence';
import { getLocalizedRegionName } from '@/lib/region-names';

import { Message, useChat } from 'ai/react';
import { generateId, type Attachment } from 'ai';
import { prepareImageAttachment, ImageTooLargeError } from '@/lib/image-attachment';
import {
  attachmentKinds,
  toChatAttachments,
  withImage,
  withLink,
  withNote,
  type AddAttachmentResult,
  type ComposerAttachment,
} from './text-search/composer-attachments';
import {
  fetchLinkPreview,
  getLinkPreviewSnapshot,
  holdLinkPreview,
  primeLinkPreview,
  releaseLinkPreview,
  retryForbiddenLinkPreviews,
} from '@/lib/link-preview/link-preview-cache';
import {
  chatAttachmentsFromTranscript,
  previewsFromTranscriptAttachments,
  type ChatTurnAttachment,
} from '@/lib/chat-transcript-attachments';

import { waitForSearchCompletion } from '@/server/search/wait-for-search';
import {
  STREAM_SEARCH_CHAT_ID,
  STREAM_SEARCH_CHAT_ID_B,
} from '@/public/constant';
import { updateUrlWithQuery } from '@/hooks/use-updated-query';
import { autoSearchKey, startGatedAutoSearch } from '@/lib/not-found/auto-search-gate';
import { RadialGradientBackground } from '../radial-gradient-background';
import { useRouter, usePathname } from 'next/navigation';
import { TextAreaSearchBar } from './text-search/text-area-search-bar';
import { GEMINI_FLASH_MODEL } from 'shared/src/lib/models';
import { selectLocalizedUrl } from '@/lib/locale-url-selector';
import { AgeVerification } from '../age-verification';
import { getUserLocation, getRegionForPrompt, type UserLocation } from '@/lib/user-location';
import { readSubregionPrefForCountry, type SubregionPrefTransport } from '@/lib/region-selection';
import { DEFAULT_REGION_PRIORITY } from '@/lib/subregion-pref';
import { resolveShelfScopeCode, type ShelfSubregionScope } from '@/lib/shelf-region-scope';
import { CityRegionSelector } from './city-region-selector';
import { failedSearchMessage, getSearchUiContent } from '@/lib/ui-content/search-ui-content';
import { parseRegionCheckResult, fallbackRegionNeeded, type RegionCheckResult } from '@/lib/region-needed-prompt';
import { useViewingProduct } from './use-viewing-product';

/**
 * MATCH QUALITY GATES — see shared/src/lib/search-gates.ts.
 *
 * These used to be two absolute points on the additive relevance score (7000
 * auto-redirect / 5000 display). That score is unbounded and its scale depends
 * on the query, so one number could not serve two searches: measured, a 1-word
 * query reached 25000-29000 while a 4-word query topped out near 7000. The
 * failure that exposed it — "veste de sauvetage pour kayak" auto-redirecting to
 * a heated women's jacket at 7200, earned by matching `veste` and `pour`.
 *
 * They are now bounded [0,1] coverage/specificity measures computed by the
 * search_url_mappings_v2 RPC. `passesDisplayGate` and `canAutoRedirect` fall
 * back to the old absolute numbers whenever a hit carries no coverage, so this
 * component never reads an env var and SEARCH_COVERAGE=off reverts it live.
 */

/** Stable empty set so "no restored turns" never churns referential equality. */
const EMPTY_TURN_IDS: ReadonlySet<string> = new Set<string>();

/**
 * Returns true when a localized href matches the requested price tier.
 * - '$'  → href must end with '-budget'
 * - '$$$' → href must end with '-premium'
 * - '$$' (or null/undefined) → href must NOT end with '-budget' or '-premium' (the default page)
 */
function hrefMatchesPriceTier(href: string, priceRange: PriceRange | null | undefined): boolean {
  const effective = priceRange ?? '$$';
  if (effective === '$') return href.endsWith('-budget');
  if (effective === '$$$') return href.endsWith('-premium');
  return !href.endsWith('-budget') && !href.endsWith('-premium');
}

/**
 * Client-side budget object. `channel` (never persisted) resolves precedence:
 * a number typed into the budget chip ('chip') beats the deterministic parser,
 * which the AI extraction may only UPGRADE (it re-expresses the same user
 * statement, adding currency, the units it covers and "not a dollar over").
 */
type ClientBudget = BudgetSpec & {
  channel: 'parser' | 'ai' | 'chip';
};

/** Strip client-only fields down to the persisted BudgetSpec. */
function toBudgetSpec(budget: ClientBudget | null): BudgetSpec | undefined {
  if (!budget) return undefined;
  const { channel, ...spec } = budget;
  return { ...spec, source: channel === 'chip' ? 'ui' : 'stated' };
}

// Props type
type SearchBarProps = {
  currentPriceRange?: PriceRange;
  // enableVoiceSearchByDefault?: boolean; // COMMENTED OUT: Voice assistant feature
  header?: boolean;
  /**
   * Keep header-search behaviour (dropdowns, minimize) but stay in document
   * flow so a sibling chrome cluster can sit beside the bar.
   */
  embedded?: boolean;
  initLocale: LocaleDetails;
  title?: string;
  topMenu?: boolean;
  productTitles?: string[];
  onSearchStateChange?: (isSearching: boolean, isVoiceSearch: boolean, isLoading: boolean, hasContent: boolean, showingMap?: boolean) => void;
  initialQuery?: string; // Query to auto-populate and trigger search
  /** Run `initialQuery` on mount, once, for a human tab: `?q=…&autoSearch=true` (the extension, other stores' search links, the SearchAction). Gated client-side by lib/not-found/auto-search-gate.ts. */
  autoSearch?: boolean;
  /** Fills the box without submitting (404 recovery). Do not use initialQuery here. */
  prefillQuery?: string;
  /**
   * Submit `prefillQuery` on mount, once, for a human tab (404 recovery: the
   * server saw a human user-agent and wants the search to start right away).
   * Separate from `autoSearch`/`initialQuery` on purpose: those also arm the
   * search-return-stash resume and rewrite the URL, neither of which belongs
   * on a dead URL. Gated client-side by lib/not-found/auto-search-gate.ts.
   */
  submitPrefill?: boolean;
  compactMode?: boolean; // Hide title and extra elements for bottom bar
  tutorialMode?: boolean; // Tutorial mode - auto-display demo
  onSearchFocus?: () => void; // Called when user focuses on search bar (to dismiss tutorial callouts)
};

export const SearchBar = forwardRef<HTMLDivElement, SearchBarProps>(function SearchBar({
  // enableVoiceSearchByDefault, // COMMENTED OUT: Voice assistant feature
  header,
  embedded,
  initLocale,
  title,
  topMenu,
  productTitles,
  onSearchStateChange,
  initialQuery,
  autoSearch,
  prefillQuery,
  submitPrefill,
  compactMode,
  tutorialMode = false,
  onSearchFocus,
}, ref) {
  const router = useRouter();
  const pathname = usePathname();

  /* -----------------------------------------------------------
   * Refs
   * --------------------------------------------------------- */
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const isBypassingAlgolia = useRef<boolean>(false); // Track when user explicitly bypasses Algolia
  const initialQueryProcessedRef = useRef<string | null>(null); // Track if initialQuery was already processed
  const lastProcessedInputRef = useRef<string | null>(null); // Track last input that triggered a search to prevent duplicates
  const productSearchRetryCountRef = useRef(0); // Retry count when product-search JSON parse fails
  const lastUserInputRef = useRef<string>(''); // Track last submitted user input for error reporting
  const searchMapCreationInFlightRef = useRef(false); // True while createSearchMap is actively running (gates the stuck-loading watchdog)
  // Bumped by every Stop and every reset. Page creation reads it when it starts
  // and drops whatever the server sends back once it has changed: a server call
  // cannot be recalled, but its answer must not open a page the shopper stopped
  // (or walked away from with Back) seconds earlier.
  const searchRunRef = useRef(0);
  const stuckRecoveryAttemptedRef = useRef(false); // Prevents the stuck-loading watchdog from firing more than once per episode
  const lastFetchedQueryRef = useRef<string | null>(null); // `${searchQuery}|${isDoneFinal}` of the last searchPages call — the live shelf refetches when the AI refines the query
  // Automatic re-runs of a shelf lookup the database could not answer: which
  // lookup they belong to (its fetchKey), how many were made, and the pause
  // before the next one. See lib/search/shelf-degraded-policy.ts.
  const shelfRetryRef = useRef<{
    key: string | null;
    attempt: number;
    timer: ReturnType<typeof setTimeout> | null;
    /** The re-runs for `key` are spent: the lookup is not asked again by itself. */
    gaveUp: boolean;
  }>({ key: null, attempt: 0, timer: null, gaveUp: false });
  // Which step failed while the failure banner is up. Decides what Retry
  // repeats, and whether the shopper's words go back into the composer.
  const failedStageRef = useRef<FailedSearchStage | null>(null);
  // A turn the network dropped gets one automatic retry; this is that turn's.
  const networkRetryUsedRef = useRef(false);
  // The turn failed while the browser was offline: retried when it comes back.
  const failedOfflineRef = useRef(false);
  // The last answer on screen when a turn failed. A DIFFERENT answer parsing
  // later means the retry worked, and the banner comes down.
  const failedOverTurnIdRef = useRef<string | null>(null);
  // The current Retry handler, for the `online` listener registered once.
  const retryHandlerRef = useRef<() => void>(() => {});
  // Guards resumeChat against firing twice for the same checkpoint (dev StrictMode
  // double-invokes effects; the caller-side ref in text-area-search-bar.tsx isn't
  // enough on its own since a real dependency change can also legitimately re-run
  // that effect). A second call for the identical (transcriptId, turnIndex) would
  // otherwise re-run resetAllStates() and race the first call's state updates —
  // e.g. clobbering the just-restored shelf snapshot back to empty.
  const lastResumedRef = useRef<string | null>(null);
  // Latest AI-decided search scope ('kit' = multi-item project). Sticky per the prompt;
  // read at Q&A-done to route kit conversations to startKitSearch instead of createSearchMap.
  const currentSearchScopeRef = useRef<SearchScope>('single');
  // Cumulative element topics the user explicitly confirmed on kit turns (AI-emitted
  // "confirmedElements" field). Read at Q&A-done so decomposition never drops them.
  const confirmedElementsRef = useRef<string[]>([]);
  // Cumulative conversational brief (constraints, rejections, image findings) — the
  // half of the intent `searchQuery` cannot hold. Same ref treatment as
  // confirmedElements so every handoff path, including the degraded ones, can reach it.
  const searchBriefRef = useRef<SearchBrief | null>(null);

  /* -----------------------------------------------------------
   * Local State
   * --------------------------------------------------------- */
  const [locale, setLocale] = useState<LocaleDetails>(initLocale);
  const [algoliaMatchs, setAlgoliaMatchs] = useState<AlgoliaHit[] | AlgoliaHit | undefined>();
  // The shelf lookup for the latest (non-concluding) turn came back with
  // nothing to show. Set only where that lookup settles, never while it is in
  // flight, so the shelf slot can say "no page fits" without first flashing it
  // over results that are about to land.
  const [shelfSettledEmpty, setShelfSettledEmpty] = useState(false);
  // Bumped to run the shelf lookup again for the turn on screen: the automatic
  // re-run after a degraded answer, or the shopper pressing Retry.
  const [shelfRetryTick, setShelfRetryTick] = useState(0);
  // Pages that exist in the region's source language but not yet in the
  // shopper's. Kept OUT of `algoliaMatchs` until display time so they can
  // never reach the auto-redirect pool.
  const untranslatedHitsRef = useRef<AlgoliaHit[]>([]);
  // Pages holding the specific products the conversation is about (the model's
  // `focusProducts`), from the last shelf fetch. When present they ARE the
  // shelf: the shopper named products, so pages that merely share words with
  // the conversation (a stylus page for "iPad Pro vs Air") stay off it.
  const focusHitsRef = useRef<AlgoliaHit[]>([]);
  // The ordinary ranked hits of the last fetch. Auto-redirect keeps deciding on
  // these while the focus pages are what the shelf displays.
  const rankedHitsRef = useRef<AlgoliaHit[]>([]);
  // The streaming turn whose named products are being looked up early (the
  // mid-stream focus prefetch); cleared once the end-of-turn fetch settles.
  const focusPrefetchKeyRef = useRef<string | null>(null);
  // The streaming turn's shelf lookup, started as soon as its inputs are final
  // (see the mid-stream effect): the end-of-turn fetch awaits it instead of
  // asking again, but only when it would have sent exactly these arguments.
  const shelfPrefetchRef = useRef<{
    argsKey: string;
    messageId: string;
    promise: Promise<SearchPagesResult>;
  } | null>(null);
  // The streamed shelf lookup on screen (POST /api/search/shelf): the turn it
  // is for, the cards it has shown, and how to stop it when a newer lookup or
  // a new send replaces it.
  const shelfStreamRef = useRef<{ messageId: string; controller: AbortController; hits: AlgoliaHit[] } | null>(null);
  // The end-of-turn lookup being awaited (`${message id}|${argsKey}`): streamed
  // cards re-run the end-of-turn effect, which must not start it again.
  const shelfLookupInFlightRef = useRef<string | null>(null);
  // Assistant-message ids rehydrated by resumeChat. Those turns render fully-formed
  // (no typewriter replay) — the user already read them before the refresh.
  const [restoredTurnIds, setRestoredTurnIds] = useState<ReadonlySet<string>>(EMPTY_TURN_IDS);
  const [enterPressed, setEnterPressed] = useState(false);
  const [finalResult, setFinalResult] = useState<
    { map: CategoryMap; locale: LocaleDetails; bestProducts: string[]; url: string } | undefined
  >();
  const [isDoneFinal, setIsDoneFinal] = useState<boolean | undefined>(undefined);
  const [isSearching, setIsSearching] = useState(false);
  const [result, setResult] = useState<
    { map: CategoryMap; bestProducts: string[]; locale: LocaleDetails; url: string } | undefined
  >();
  const [searchInput, setSearchInput] = useState(prefillQuery ?? '');
  const [aiGeneratedQuery, setAiGeneratedQuery] = useState(''); // Separate state for AI-generated query
  const [isDemo, setIsDemo] = useState(false);
  const [demoType, setDemoType] = useState<'product' | 'category' | null>(null); // Track which demo type was clicked
  const [showNote, setShowNote] = useState(false);
  const [searchRejectedMessage, setSearchRejectedMessage] = useState<string | null>(null);
  // Epoch ms before which Retry is pointless: the limiter's window after a 429.
  // The pill counts it down instead of sending a request refused the same way.
  const [retryNotBefore, setRetryNotBefore] = useState<number | null>(null);
  // const [isVoiceSearch, setIsVoiceSearch] = useState(enableVoiceSearchByDefault ?? false); // COMMENTED OUT: Voice assistant feature
  const [isVoiceSearch, setIsVoiceSearch] = useState(false); // Disabled voice search
  const [focusingOnTextArea, setFocusingOnTextArea] = useState(false);
  // const enableVoiceSearchForTesting = false; // COMMENTED OUT: Voice assistant feature

  // Gates the focus-triggered second background layer (below) to desktop only.
  // On phones, focusing the bar already pays for a layout morph into the
  // composer capsule plus several state updates through this + the child
  // component's tree; stacking a second canvas particle system + RAF loop on
  // top of the page's existing one in that same frame is what actually reads
  // as janky "flip" performance. Desktop keeps the extra flourish — it's the
  // effect the design wants, just not doubled up on constrained hardware.
  const [isDesktopViewport, setIsDesktopViewport] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const update = () => setIsDesktopViewport(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  // What waits in the bar to be sent: a photo, links, notes (the "+" menu or a
  // paste). The ref mirrors the state for the send paths, which run a render
  // after the click. A photo's downscale to a data URL starts the moment it is
  // picked (preparedImagesRef, by attachment id) so sending only awaits it.
  const [attachments, setAttachmentsState] = useState<ComposerAttachment[]>([]);
  const attachmentsRef = useRef<ComposerAttachment[]>([]);
  const preparedImagesRef = useRef(new Map<string, Promise<Attachment>>());
  // Bumped to send attachments with no words: the input effect cannot fire for "".
  const [attachmentSubmitTick, setAttachmentSubmitTick] = useState(0);

  // Two-agent system state
  const [currentSearchIntent, setCurrentSearchIntent] = useState<SearchIntent | null>(null);
  const [isClassifyingIntent, setIsClassifyingIntent] = useState(false);
  const [currentClassification, setCurrentClassification] = useState<any>(null); // Store full classification result
  const [intentCache, setIntentCache] = useState<Map<string, any>>(new Map()); // Cache full classification, not just intent

  // The paid helper (/api/check-region-needed) answers only a browser that
  // already cleared BotID (server/search/search-helper-pass.ts). It used to
  // fire beside /api/product-search, so every refused bot paid for a model
  // call first. Now a search parks it here and it starts when product-search
  // answers, carrying the pass it returned.
  // A link pasted before the first search can be refused for want of a search
  // proof: the first admitted turn mints one, and those previews ask again.
  const [searchHelperGate] = useState(() =>
    createSearchHelperGate({ onPass: (headers) => retryForbiddenLinkPreviews(headers) }),
  );

  // Pre-buy seed: the exact product the user was buying when this conversation
  // opened (set by the buy-button morph flow). Rides the chat body so the AI
  // completes the project AROUND it, and pins element 0 of the resulting kit.
  const [seedProduct, setSeedProduct] = useState<KitSeedProduct | null>(null);
  // Bumped each time a seed opens a conversation here, so the composer brings
  // back a panel the shopper minimized earlier (see TextAreaSearchBar).
  const [revealConversationTick, setRevealConversationTick] = useState(0);

  // Pending product-type create-new-search context (when user sees price-mismatched Algolia results)
  const pendingCreateNewSearchRef = useRef<{ userInput: string; searchType: any } | null>(null);
  // The shelf a "Create a new page" tap left, so Stop can put the shopper back
  // on it. Null when the page being built came from a send.
  const buildFromShelfRef = useRef<{
    matches: AlgoliaHit | AlgoliaHit[] | undefined;
    pendingCreate: typeof pendingCreateNewSearchRef.current;
  } | null>(null);

  // Region selection state
  const [regionCheckResult, setRegionCheckResult] = useState<RegionCheckResult | null>(null);
  const [needsRegionSelection, setNeedsRegionSelection] = useState(false);
  const [showRegionSelector, setShowRegionSelector] = useState(false);
  const [userRegion, setUserRegion] = useState<string | null>(null);
  /**
   * The DURABLE subregion preference, as it travels to the server.
   *
   * Deliberately separate from `userRegion`: that one is a per-conversation
   * gate (see `userExplicitlyChoseRegion`) that drives regional page creation,
   * and it must keep meaning exactly what it means today. This is the standing
   * preference — what the shopper told us once, across sessions and origins.
   */
  const [subregionFirstPref, setSubregionFirstPref] = useState<SubregionPrefTransport | undefined>(undefined);
  /**
   * The preference above, resolved by the SERVER to the canonical admin-1 code
   * hit tags carry (searchPagesWithStatus does the registry lookup the boost
   * already pays for). Null = nationwide / no preference. Drives the shelf's
   * scope chip and regional grouping; never affects retrieval or redirects.
   */
  const [shelfSubregionCode, setShelfSubregionCode] = useState<string | null>(null);
  const [userExplicitlyChoseRegion, setUserExplicitlyChoseRegion] = useState(false);
  const chatUserRegion = userExplicitlyChoseRegion && userRegion ? userRegion : undefined;
  const [regionSelectionCompleted, setRegionSelectionCompleted] = useState(false);
  const [pendingSearchInput, setPendingSearchInput] = useState<string | null>(null);

  // Pages are no longer tiered by the shopper: every search asks for the
  // default page, and a stated number (below) is how price enters a search.
  // The tier plumbing downstream still carries this constant until it goes.
  const selectedPriceRange: PriceRange = '$$';
  const priceRangeSource: PriceRangeSource = 'default';

  // Numeric budget: per-search intent, NEVER persisted to localStorage (it is a
  // fact about this purchase, not a taste). State drives the chip; the ref
  // mirrors it so the async done-branch below never races React state.
  // Hydration-safe: starts null, only ever set client-side after a submit.
  const [budget, setBudgetState] = useState<ClientBudget | null>(null);
  const budgetRef = useRef<ClientBudget | null>(null);
  // Set when the shopper empties the chip: the number they stated earlier is
  // still in the conversation, and neither the parser nor the AI may bring it
  // back until they state a new one.
  const budgetClearedRef = useRef(false);
  const applyBudget = (next: ClientBudget | null) => {
    budgetRef.current = next;
    setBudgetState(next);
  };

  // Parallel execution state - store results from all three parallel requests
  const [parallelResults, setParallelResults] = useState<{
    classification?: any;
    productSearch?: any;
    pageQuestion?: any;
  }>({});
  const [isParallelExecuting, setIsParallelExecuting] = useState(false);

  // Product-type search: show loading-clean while Algolia/createSearchMap run (not when redirecting)
  const [isProductBranchLoading, setIsProductBranchLoading] = useState(false);

  // True while we're silently checking Algolia for a possible redirect (product searchType).
  // Suppresses the loading UI so the user never sees the spinner if we're going to redirect.
  const [isCheckingAlgoliaForRedirect, setIsCheckingAlgoliaForRedirect] = useState(false);
  // True while the approximate→product resolver is actively browsing the web (surfaces a
  // localized "browsing the web" status). Set from the resolver's streamed status events.
  const [resolverBrowsing, setResolverBrowsing] = useState(false);

  // Product-search parse error: when AI returns invalid JSON and retries exhausted
  const [productSearchParseError, setProductSearchParseError] = useState(false);
  /**
   * The search could not be ANSWERED — the database timed out or errored —
   * as opposed to answering "nothing matches".
   *
   * Kept distinct from `productSearchParseError` (the AI returned unparseable
   * JSON) because the causes differ, but both render the same "Something went
   * wrong / Retry" affordance. The point of this flag is what it PREVENTS:
   * while it is set, no code path may start page generation, because an
   * unanswerable lookup is not evidence that the page is missing.
   */
  const [searchDegraded, setSearchDegraded] = useState(false);

  // Adult content detection state
  const [isAdultContent, setIsAdultContent] = useState(false);
  const [showAgeVerification, setShowAgeVerification] = useState(false);
  const [pendingAdultResult, setPendingAdultResult] = useState<
    { map: CategoryMap; bestProducts: string[]; locale: LocaleDetails; url: string } | undefined
  >();

  /* -----------------------------------------------------------
   * Chat Hooks Setup
   * --------------------------------------------------------- */
  const [turnFetch] = useState(() => boundedTurnFetch());
  // useChat shares messages and status between every hook given the same `id`,
  // in a cache that outlives client-side navigation: a fixed id hands one bar's
  // conversation to every other bar, including the top-menu bar that mounts on
  // the page a search sends the shopper to. Hook ids are per instance; the
  // `body.id` each route receives stays the fixed one.
  const chatInstanceId = useId();
  // The product the shopper is on (top bar on a product page): a hint every question
  // carries, so "is this one good?" means the card on screen (lib/ai-search/viewing-hint.ts).
  const viewingHint = useViewingProduct(Boolean(topMenu)) ?? undefined;
  // Product search chat (for finding new products)
  const { messages, setMessages, handleSubmit, input, setInput, stop, isLoading, append, reload } = useChat({
    api: '/api/product-search',
    id: `${STREAM_SEARCH_CHAT_ID}-${chatInstanceId}`,
    body: {
      id: STREAM_SEARCH_CHAT_ID,
      modelId: GEMINI_FLASH_MODEL,
      locale,
      productTitles,
      pageContext: title,
      searchType: currentClassification?.searchType,
      seedProduct: seedProduct ?? undefined,
      // Rides EVERY product-search turn (useChat merges this hook-level body
      // under each per-call body), alongside — never instead of — `userRegion`.
      // `undefined` is dropped by JSON.stringify, so a visitor with no stated
      // preference sends a byte-identical body to today's.
      subregionPref: subregionFirstPref,
      viewingHint,
    },
    initialMessages: [], // Start empty
    // Bounds the turn in time: the wait for its response to start, and every
    // gap in its stream. Without it a request that never leaves the browser
    // (the bot check waiting on a script the connection could not load) or a
    // stream cut by a lost connection spins forever, with nothing to press.
    fetch: turnFetch,
    // Runs as soon as the turn's headers arrive, before the stream: the gate
    // has spoken. Admitted → keep the fresh pass and start the parked helpers;
    // refused or failed → they never run.
    onResponse: (response) => searchHelperGate.admit(response),
    onFinish: () => { },
    onError: (error) => {
      // A gate refusal is not a failure — see isDeliberateRefusal.
      const refused = isDeliberateRefusal(error);
      // A stream that broke part-way leaves a half-written answer as the last
      // message. Drop it, so the conversation ends on the message that went
      // unanswered: Retry then regenerates that one, and the parse-failure
      // path does not quietly reload a truncated turn behind the banner.
      if (!refused) {
        setMessages((current) => (current[current.length - 1]?.role === 'assistant' ? current.slice(0, -1) : current));
      }
      // The network dropped the request: ask once more before saying anything.
      if (!refused && isNetworkFailure(error) && retryDroppedTurn()) return;
      failedOfflineRef.current = !refused && typeof navigator !== 'undefined' && navigator.onLine === false;
      if (!refused) {
        reportSearchFailure('product-search-stream-error', { errorMessage: error?.message, userInput: lastUserInputRef.current || undefined });
      }
      // A non-200 (403/429 from the API guard, or any 5xx) used to leave every
      // in-flight flag stuck and the spinner spinning forever. Degrade instead:
      // clears the flags and raises the translated retry affordance. This runs
      // for refusals too, which say plainly that the search could not run.
      failSearchAsDegraded(refused ? 'refused' : 'failed', 'turn', refusalRetryAfterSeconds(error));
    },
  });

  // Page question chat (for contextual page questions)
  const {
    messages: pageMessages,
    setMessages: setPageMessages,
    handleSubmit: handlePageSubmit,
    input: pageInput,
    setInput: setPageInput,
    stop: stopPageChat,
    isLoading: isPageLoading,
    append: appendPageMessage,
  } = useChat({
    api: '/api/page-question',
    id: `stream-page-question-${chatInstanceId}`,
    body: {
      id: 'stream-page-question',
      modelId: GEMINI_FLASH_MODEL,
      locale,
      productTitles,
      pageContext: title,
      viewingHint,
    },
    initialMessages: [],
    onFinish: (message) => {
      // Page questions don't trigger search - they answer directly
      try {
        const response = JSON.parse(message.content);
      } catch (error) {
        // Error parsing page question response
      }
    },
    onError: (error) => {
      // Same reasoning as product-search: /api/page-question sits behind the
      // same guard, so its refusals are not failures either.
      if (!isDeliberateRefusal(error)) {
        reportSearchFailure('page-question-stream-error', { errorMessage: error?.message, userInput: lastUserInputRef.current || undefined });
      }
      setIsSearching(false);
      setEnterPressed(false);
    },
  });

  const {
    messages: searchPropositions,
    setMessages: setSearchPropositions,
    handleSubmit: handleSearchPropositionSubmit,
    stop: stopSearchProposition,
    setInput: setSearchPropositionInput,
  } = useChat({
    api: undefined, //topMenu ? undefined : '/api/search-suggestions', // Disable search suggestions when topMenu is true
    id: `${STREAM_SEARCH_CHAT_ID_B}-${chatInstanceId}`,
    body: { id: STREAM_SEARCH_CHAT_ID_B, modelId: GEMINI_FLASH_MODEL, locale },
    onFinish: () => { },
  });

  /* -----------------------------------------------------------
   * Chat Transcript Persistence (for resumable conversations)
   * --------------------------------------------------------- */
  const {
    resetChatId: resetTranscriptChatId,
    adoptChatId: adoptTranscriptChatId,
    finalize: finalizeTranscript,
  } = useChatTranscriptPersistence({
    messages,
    locale: locale.path,
    enabled: currentSearchIntent === 'product-search' || messages.length > 0,
    pageContext: {
      pageContext: title,
      productTitles,
      shouldUsePageContext: currentClassification?.shouldUsePageContext,
    },
    classification: currentClassification
      ? {
          searchType: currentClassification.searchType,
          intent: currentClassification.intent,
        }
      : undefined,
    budget: budget
      ? {
          max: budget.max,
          min: budget.min,
          currency: budget.currency,
          stretch: budget.stretch,
          quantity: budget.quantity,
        }
      : undefined,
  });

  /* -----------------------------------------------------------
   * Helper functions
   * --------------------------------------------------------- */

  const reportSearchFailure = (
    failureType: 'stream-never-started' | 'classify-intent-error' | 'product-search-stream-error' | 'page-question-stream-error' | 'parallel-executing-stuck',
    extra?: { errorMessage?: string; userInput?: string }
  ) => {
    fetch('/api/search-failure-alert', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        failureType,
        locale: locale?.path,
        isLoading,
        isParallelExecuting,
        isPageLoading,
        ...extra,
      }),
    }).catch(() => {});
  };

  // The conversation as it is NOW, for callbacks that run later than the
  // render that created them (timers, the `online` listener).
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  /** Forget the pending and past automatic re-runs of the shelf lookup. */
  const clearShelfRetry = () => {
    if (shelfRetryRef.current.timer) clearTimeout(shelfRetryRef.current.timer);
    shelfRetryRef.current = { key: null, attempt: 0, timer: null, gaveUp: false };
  };

  /**
   * Ask the model again for the conversation as it stands, whose last message
   * is the one that went unanswered. Never `append`: that would send the
   * shopper's words a second time.
   */
  const reloadTurn = () => {
    setIsSearching(true);
    reload({
      body: {
        id: STREAM_SEARCH_CHAT_ID,
        modelId: GEMINI_FLASH_MODEL,
        locale,
        productTitles,
        pageContext: title,
        shouldUsePageContext: currentClassification?.shouldUsePageContext ?? false,
        userRegion: userExplicitlyChoseRegion && userRegion ? userRegion : undefined,
        searchType: currentClassification?.searchType,
      },
    });
  };

  /**
   * The network dropped a turn's request before any answer came back. One
   * automatic retry, after a short pause, before the shopper is shown anything:
   * a phone changing cells loses a request far more often than it loses two.
   * Returns false when this turn already had its retry, or the browser says it
   * is offline (that failure is shown, and retried when the connection returns).
   */
  const retryDroppedTurn = (): boolean => {
    if (networkRetryUsedRef.current) return false;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
    networkRetryUsedRef.current = true;
    setTimeout(() => {
      // Only if the unanswered message is still the conversation's last one:
      // the shopper may have cancelled or started over during the pause.
      const pending = messagesRef.current;
      if (pending[pending.length - 1]?.role === 'user') reloadTurn();
    }, NETWORK_RETRY_DELAY_MS);
    return true;
  };

  const resetAllStates = () => {
    stop();
    stopPageChat(); // Stop page question chat
    setIsSearching(false);
    setIsDoneFinal(false);
    setIsDemo(false);
    setDemoType(null);
    setMessages([]);
    setPageMessages([]); // Clear page question messages
    setShelfSettledEmpty(false);
    setEnterPressed(false);
    setSearchInput('');
    setAiGeneratedQuery(''); // Clear AI-generated query
    setSearchRejectedMessage(null);
    clearAttachments({ sent: false });
    setAlgoliaMatchs(undefined);
    focusHitsRef.current = [];
    rankedHitsRef.current = [];
    // A lookup still in flight must not bring the shelf back after a reset.
    focusPrefetchKeyRef.current = null;
    setResult(undefined); // Clear search result
    setFinalResult(undefined); // Clear final result
    setRestoredTurnIds(EMPTY_TURN_IDS); // Fresh turns animate again

    // Clear search intent and classification to start fresh
    setCurrentSearchIntent(null);
    setCurrentClassification(null);
    setIsClassifyingIntent(false);
    setIsParallelExecuting(false);
    setIntentCache(new Map()); // Clear intent cache
    currentSearchScopeRef.current = 'single'; // Reset kit-scope escalation
    confirmedElementsRef.current = [];
    searchBriefRef.current = null; // A previous conversation's constraints must not leak forward
    setSeedProduct(null); // A fresh search is no longer anchored to a pre-buy product

    // Clear parallel results
    setParallelResults({});

    // Clear region selection state
    searchHelperGate.cancel();
    setRegionCheckResult(null);
    setShowRegionSelector(false);
    setNeedsRegionSelection(false);
    setPendingSearchInput(null);
    setUserExplicitlyChoseRegion(false);
    setRegionSelectionCompleted(false);

    // Budget is per-search — a fresh search starts with none
    applyBudget(null);
    budgetClearedRef.current = false;

    stopSearchProposition();
    setSearchPropositions([]);
    inputRef.current?.blur();

    setIsProductBranchLoading(false);
    setProductSearchParseError(false);
    // A failure belongs to the conversation that had it. Left set, a resumed
    // conversation opened on the previous one's error banner, and its Retry
    // sent the previous one's words.
    setSearchDegraded(false);
    failedStageRef.current = null;
    failedOfflineRef.current = false;
    networkRetryUsedRef.current = false;
    lastUserInputRef.current = '';
    clearShelfRetry();
    productSearchRetryCountRef.current = 0;
    searchMapCreationInFlightRef.current = false;
    stuckRecoveryAttemptedRef.current = false;
    // A page still being built belongs to the search being reset.
    searchRunRef.current += 1;
    buildFromShelfRef.current = null;

    // Reset locale to initial locale when canceling search
    setLocale(initLocale);

    // Reset input tracking ref so the same query can be searched again
    lastProcessedInputRef.current = null;
    lastFetchedQueryRef.current = null;
    lastResumedRef.current = null;

    // Reset transcript chatId so the next chat starts a fresh persisted record
    resetTranscriptChatId();
  };

  /**
   * Drop a concluded (or restored) product-search conversation so the next
   * typed query is a first search, not an answer appended onto the old one.
   * Lighter than resetAllStates: keeps the composer text and the in-flight
   * submit, which that helper would blur and clear.
   */
  const beginFreshProductSearch = () => {
    stop();
    setMessages([]);
    setIsDoneFinal(undefined);
    setAlgoliaMatchs(undefined);
    setShelfSettledEmpty(false);
    setShelfSubregionCode(null);
    untranslatedHitsRef.current = [];
    focusHitsRef.current = [];
    rankedHitsRef.current = [];
    focusPrefetchKeyRef.current = null;
    setCurrentSearchIntent(null);
    setCurrentClassification(null);
    setRestoredTurnIds(EMPTY_TURN_IDS);
    lastFetchedQueryRef.current = null;
    lastResumedRef.current = null;
    lastProcessedInputRef.current = null;
    productSearchRetryCountRef.current = 0;
    currentSearchScopeRef.current = 'single';
    confirmedElementsRef.current = [];
    searchBriefRef.current = null;
    pendingCreateNewSearchRef.current = null;
    buildFromShelfRef.current = null;
    isBypassingAlgolia.current = false;
    resetTranscriptChatId();
    setResult(undefined);
    setFinalResult(undefined);
    setIsProductBranchLoading(false);
    setProductSearchParseError(false);
    setSearchDegraded(false);
    setSearchRejectedMessage(null);
    failedStageRef.current = null;
    failedOfflineRef.current = false;
    networkRetryUsedRef.current = false;
    clearShelfRetry();
    searchHelperGate.cancel();
    applyBudget(null);
    budgetClearedRef.current = false;
  };

  const cancelSearch = () => {
    // Reset the bypass flag when canceling search
    isBypassingAlgolia.current = false;
    resetAllStates();
    // Explicit "start over" — don't let a later Back resurrect this conversation.
    // Deliberately not in resetAllStates itself: that also runs on the popstate/
    // pathname-change resets, where clearing the stash would break a legitimate
    // forward-then-back-again restore.
    clearSearchReturnStash();
  };

  /**
   * The loading row's Stop. The page being built is dropped (searchRunRef:
   * whatever the server sends back is ignored) and the shopper lands on the
   * moment before it started:
   * - from a shelf's "Create a new page": back on that shelf;
   * - from a send: that message is un-sent. Its words go back in the bar and
   *   the conversation returns to the turn before it (none, for an opening
   *   message).
   * It never throws away the rest of the conversation. Starting over stays
   * cancelSearch, behind the bar's X and the shelf's "New search".
   */
  const stopSearch = () => {
    searchRunRef.current += 1;
    searchMapCreationInFlightRef.current = false;
    stuckRecoveryAttemptedRef.current = false;
    isBypassingAlgolia.current = false;
    setIsProductBranchLoading(false);
    setResult(undefined);
    setFinalResult(undefined);

    const shelf = buildFromShelfRef.current;
    buildFromShelfRef.current = null;
    if (shelf) {
      pendingCreateNewSearchRef.current = shelf.pendingCreate;
      setAlgoliaMatchs(shelf.matches);
      setIsDoneFinal(true);
      return;
    }

    // Read before cancelSearch below clears it.
    const words = lastUserInputRef.current;
    const lastUserIndex = messages.findLastIndex((m) => m.role === 'user');
    if (lastUserIndex <= 0) {
      // The opening message (or a classified fast path, which has no chat):
      // back to the bar as it was before the first send.
      cancelSearch();
    } else {
      stop();
      setMessages(messages.slice(0, lastUserIndex));
      setIsDoneFinal(false);
      setAlgoliaMatchs(undefined);
      setShelfSettledEmpty(false);
      clearShelfRetry();
      // The turn now last is read again by the message effect: its shelf is
      // looked up again, and these three, which the AI carries across turns,
      // come back as that turn left them (same reset as editing a turn).
      lastFetchedQueryRef.current = null;
      currentSearchScopeRef.current = 'single';
      confirmedElementsRef.current = [];
      searchBriefRef.current = null;
      // The same words can be sent again.
      lastProcessedInputRef.current = null;
      lastUserInputRef.current = words;
    }
    setSearchInput(words);
  };

  /**
   * Resume a previously persisted chat from the assistant turn at `turnIndex`.
   * Rehydrates useChat.messages, restores classification snapshot and
   * aiGeneratedQuery, and adopts the existing transcript id so future updates
   * overwrite that record (effectively truncating any later turns).
   *
   * If a chat is currently in progress, asks the user for confirmation before
   * discarding it.
   */
  const resumeChat = async ({
    transcriptId,
    turnIndex,
  }: {
    transcriptId: string;
    turnIndex: number;
  }) => {
    const resumeKey = `${transcriptId}|${turnIndex}`;
    if (lastResumedRef.current === resumeKey) return;
    lastResumedRef.current = resumeKey;

    const transcript: ChatTranscript | null = getChatTranscript(transcriptId);
    if (!transcript) {
      console.warn('resumeChat: transcript not found', transcriptId);
      return;
    }

    if (messages.length > 0 && !isDoneFinal) {
      const confirmDiscard = typeof window !== 'undefined'
        ? window.confirm('Discard the current chat and resume the selected one?')
        : true;
      if (!confirmDiscard) return;
    }

    // Wipe live state but keep the resumed transcript id alive
    resetAllStates();

    const keptTurns = transcript.turns.slice(0, turnIndex + 1);
    if (keptTurns.length === 0) {
      console.warn('resumeChat: no turns to resume');
      return;
    }

    // Rebuild Vercel AI SDK Message[] from the transcript.
    // Layout: user(initial) -> assistant(turn0) -> [user(turn1.userInput) -> assistant(turn1)] ...
    // Links and notes come back on their messages (the model reads them again,
    // the thread shows their cards); their saved previews seed the card store.
    for (const [url, preview] of previewsFromTranscriptAttachments([
      transcript.initialAttachments,
      ...keptTurns.map((turn) => turn.userAttachments),
    ])) {
      primeLinkPreview(url, preview);
    }
    const withAttachments = (items: ChatTurnAttachment[] | undefined) => {
      const restored = chatAttachmentsFromTranscript(items);
      return restored.length > 0 ? { experimental_attachments: restored } : {};
    };
    const rebuilt: Message[] = [];
    rebuilt.push({
      id: `${transcript.id}-user-initial`,
      role: 'user',
      content: transcript.initialUserInput,
      ...withAttachments(transcript.initialAttachments),
    });

    keptTurns.forEach((turn, idx) => {
      if (idx > 0 && (turn.userInput || turn.userAttachments?.length)) {
        rebuilt.push({
          id: `${transcript.id}-user-${idx}`,
          role: 'user',
          content: turn.userInput ?? '',
          ...withAttachments(turn.userAttachments),
        });
      }
      rebuilt.push({
        id: `${transcript.id}-asst-${turn.index}`,
        role: 'assistant',
        content: turn.rawAssistantContent,
      });
    });

    // Rehydrate UI state from the resumed checkpoint
    const lastTurn = keptTurns[keptTurns.length - 1];

    // Edge case: if user resumed on a different page than where the chat started,
    // strip page-context usage so subsequent turns don't mix the original page's
    // products with the current page's products.
    const snapshotPageContext = transcript.pageContextSnapshot?.pageContext ?? '';
    const snapshotProductTitlesKey = (transcript.pageContextSnapshot?.productTitles ?? []).join('|');
    const currentProductTitlesKey = (productTitles ?? []).join('|');
    const pageContextDiffers = snapshotPageContext !== (title ?? '')
      || snapshotProductTitlesKey !== currentProductTitlesKey;
    const restoredClassification = transcript.classification
      ? {
          ...transcript.classification,
          // Force page context off when current page differs from the original snapshot
          shouldUsePageContext: pageContextDiffers
            ? false
            : transcript.pageContextSnapshot?.shouldUsePageContext ?? false,
        }
      : null;

    setMessages(rebuilt);
    // Mark every rehydrated assistant turn so the banner / thread render them
    // fully-formed instead of replaying the typewriter on already-read text.
    setRestoredTurnIds(
      new Set(rebuilt.filter((m) => m.role === 'assistant').map((m) => m.id)),
    );
    setCurrentSearchIntent((transcript.classification?.intent as SearchIntent | undefined) ?? 'product-search');
    if (restoredClassification) {
      setCurrentClassification(restoredClassification);
    }
    if (lastTurn.parsed.searchQuery) {
      setAiGeneratedQuery(lastTurn.parsed.searchQuery);
    }
    // Restore the numeric budget (chip + shelf) the conversation already heard.
    if (transcript.budget?.max) {
      applyBudget({
        max: transcript.budget.max,
        min: transcript.budget.min,
        currency: transcript.budget.currency,
        source: 'stated',
        stretch: transcript.budget.stretch,
        quantity: transcript.budget.quantity,
        channel: 'parser',
      });
    }

    // A conversation that already reached its conclusion is restored AS concluded,
    // so backing out of the page it sent you to lands on the same "here's what I
    // found" state instead of a half-finished Q&A. Only when resuming the LAST turn:
    // rewinding to an earlier checkpoint means the user wants to continue from there.
    const isLastTurn = lastTurn.index === transcript.turns[transcript.turns.length - 1].index;
    const wasCompleted = isLastTurn && (!!transcript.finalHref || lastTurn.parsed.isDone);

    setIsDoneFinal(wasCompleted);
    setIsSearching(true);
    // Resume rehydrates an already-answered turn — nothing is in flight. Leaving
    // `enterPressed` true (as we used to for unfinished chats) stuck the composer
    // spinner forever: the safety timeout only clears it when messages.length === 0,
    // and no new stream runs to clear it the normal way.
    setEnterPressed(false);

    // A concluded restore should show the same shelf the user left, instantly,
    // rather than an empty panel while a fetch (if one even runs — see the
    // isRestoredTurn branch in the message-processing effect) catches up.
    if (wasCompleted && transcript.finalShelf?.length) {
      setAlgoliaMatchs(transcript.finalShelf);
    }

    // Adopt the existing transcript id so subsequent edits overwrite it
    // (and thus implicitly truncate any later turns that are no longer in `messages`).
    adoptTranscriptChatId(transcript.id);
  };

  /**
   * Park the region check until the product-search turn this search is about
   * to submit clears the bot gate (see onResponse above). Its answer is needed
   * only after the first turn has streamed, long after the gate's answer.
   */
  const deferSearchHelpers = (query: string) => {
    // A turn of attachments alone asks with what they say: the link's title
    // (once its preview is in) or the note's first line. Nothing to ask with,
    // nothing is asked (the route refuses an empty query).
    const regionQuery = query.trim() || attachmentRegionHint();
    if (!regionQuery) return;
    searchHelperGate.park({
      start: () => triggerRegionCheck(regionQuery),
      cancel: () => {},
    });
  };

  const attachmentRegionHint = (): string => {
    for (const item of attachmentsRef.current) {
      if (item.kind === 'link') {
        const preview = getLinkPreviewSnapshot(item.url).preview;
        if (preview && preview.kind !== 'unavailable' && preview.title) return preview.title.slice(0, 160);
      } else if (item.kind === 'note') {
        const firstLine = item.text.split('\n').find((line) => line.trim());
        if (firstLine) return firstLine.trim().slice(0, 160);
      }
    }
    return '';
  };

  /** Fire check-region-needed; result gates the in-search region selector. */
  const triggerRegionCheck = (searchQuery: string) => {
    fetch('/api/check-region-needed', {
      method: 'POST',
      headers: searchHelperGate.headers(),
      body: JSON.stringify({ searchQuery, locale: locale.path }),
    })
      .then((res) => res.json())
      .then((data: unknown) => {
        const parsed = parseRegionCheckResult(data);
        setRegionCheckResult(
          parsed ?? {
            needsRegion: fallbackRegionNeeded(searchQuery),
            confidence: 0.4,
            reason: 'Invalid region-check JSON',
          },
        );
      })
      .catch(() =>
        setRegionCheckResult({
          needsRegion: fallbackRegionNeeded(searchQuery),
          confidence: 0.4,
          reason: 'Error',
        }),
      );
  };

  /* -----------------------------------------------------------
   * Effect: Reset all states when navigating back (popstate)
   * This ensures the search bar returns to its initial state
   * --------------------------------------------------------- */
  useEffect(() => {
    const handlePopState = () => {
      resetAllStates();
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* -----------------------------------------------------------
   * Effect: Reset all states when pathname changes (navigation)
   * This prevents stale search state from carrying over between pages
   * --------------------------------------------------------- */
  const previousPathnameRef = useRef<string | null>(null);
  useEffect(() => {
    // Skip the initial mount - only reset on actual navigation
    if (previousPathnameRef.current !== null && previousPathnameRef.current !== pathname) {
      resetAllStates();
    }
    previousPathnameRef.current = pathname;
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  /* -----------------------------------------------------------
   * Effect: Load saved user location on mount - country-specific
   * --------------------------------------------------------- */
  useEffect(() => {
    const savedLocation = getUserLocation(locale.regionCode);
    if (savedLocation) {
      setUserRegion(savedLocation.region);
    }
    // Durable preference: the stored location first, the cookie as the fallback
    // for a visitor who just arrived from another origin (or whose localStorage
    // was cleared). Never gates anything — see the state declaration.
    setSubregionFirstPref(readSubregionPrefForCountry(locale.regionCode, savedLocation));
  }, [locale.regionCode]);

  /* -----------------------------------------------------------
   * Effect: Notify parent about search state changes and handle search content visibility
   * --------------------------------------------------------- */
  useEffect(() => {
    if (onSearchStateChange) {
      // Pass whether there's active content (input or messages) to help parent decide layout
      const hasContent = messages.length > 0 || pageMessages.length > 0;
      // 5th arg = "a panel is open above the bar" (the region map). It grows
      // upward from the bar's top edge, so the hero hides its prompt copy to
      // leave it room — see isShowingMap in main-search-bar.
      onSearchStateChange(isSearching, isVoiceSearch, isLoading, hasContent, showRegionSelector);
    }

    // COMMENTED OUT: Voice assistant feature - Hide/show search content container based on voice search state
    // if (!header) {
    //   const searchContentContainer = document.getElementById('search-content-container');
    //   if (searchContentContainer) {
    //     if (isVoiceSearch) {
    //       searchContentContainer.style.display = 'none';
    //     } else {
    //       searchContentContainer.style.display = '';
    //     }
    //   }
    // }
  }, [isSearching, isVoiceSearch, isLoading, messages.length, pageMessages.length, showRegionSelector, onSearchStateChange]);

  /* -----------------------------------------------------------
   * Effect: Sync Title Prop to Input - REMOVED
   * --------------------------------------------------------- */
  // Removed: title is page context, not search input. 
  // Setting input to title was causing auto-search on page load.

  /* -----------------------------------------------------------
   * Effect: Check if previous search was adult content
   * --------------------------------------------------------- */
  useEffect(() => {
    // Check if age is already verified
    const ageVerified = localStorage.getItem('age-verified') === 'true';
    if (ageVerified) {
      return; // User has already verified their age
    }

    // Get search history to check if previous search was adult
    const history = getSearchHistory();
    if (history.length > 0) {
      // Check the most recent search
      const mostRecentSearch = history[0];
      if (mostRecentSearch.pg18) {
        // Previous search was adult content, show verification
        setIsAdultContent(true);
        setShowAgeVerification(true);
      }
    }
  }, []); // Run only on mount

  /* -----------------------------------------------------------
   * Effect: Trigger startPageSearch on Result Update
   * --------------------------------------------------------- */
  useEffect(() => {
    if (!finalResult || typeof finalResult === 'string') return;
    const run = searchRunRef.current;

    const executeSearch = async () => {
      const finalHref = selectLocalizedUrl(finalResult.map.href, locale);
      const redirectHref = await startPageSearch(finalResult);
      // Stopped, reset or left with Back while the job was registered: the
      // shopper stays where that left them.
      if (run !== searchRunRef.current) return;
      if (redirectHref) {
        const persistedHref = stripSearchIntentQuery(redirectHref);
        // Save to search history
        const searchQuery = finalResult.map.searchTerm || finalResult.map.title;
        if (searchQuery) {
          addToSearchHistory({
            searchQuery,
            href: persistedHref,
            locale: locale.path,
            pg18: finalResult.map.pg18 || isAdultContent,
          });
        }
        // Finalize the persisted chat transcript with the final search query + redirect href
        finalizeTranscript(searchQuery || undefined, persistedHref, snapshotShelfForTranscript());
        // Don't call cancelSearch() - keep loading state visible until navigation completes
        //redirect to the new search page
        router.push(redirectHref);
      } else if (finalHref) {
        //reload
        const searchQuery = finalResult.map.searchTerm || finalResult.map.title;
        waitForCompletion(finalHref, searchQuery);
        cancelSearchRetry();
      }
    };
    executeSearch();
  }, [finalResult]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * The shelf lookup a turn asks for: the arguments sent, their key, and the
   * fetchKey the refetch gate compares. The end-of-turn fetch and its
   * mid-stream start both build it here, so for the same turn they can only
   * send the same thing.
   */
  const shelfLookupFor = (msgs: typeof messages, turn: ReturnType<typeof parseAssistantMessage>) => {
    // The shopper's opening words, searched alongside the AI's refined query
    // (see the end-of-turn fetch for why, and for openingStillFits).
    const originalUserQuery = msgs.find((m) => m.role === 'user')?.content?.trim() || undefined;
    const shelfOpening = openingWordsForShelf(originalUserQuery, turn);
    const effectiveLocale = resolveTurnLocale(turn.langCode, locale, initLocale);
    // Done-mode retrieval (larger set, separate cache key) must follow the
    // AI turn, not the React flag. `isDoneFinal` lags one effect pass, so
    // using it alone fetched the `typing` cache on the concluding turn —
    // a stale empty blob there sent "iPhone 17 Pro Max" into page creation
    // while a history-resume (already isDoneFinal) used the `done` cache
    // and showed the shelf.
    const doneMode = !!(turn.isDone || isDoneFinal);
    // The products the shopper named. Part of the key: a turn that starts
    // (or stops) naming products must refetch even with the same query.
    const focusTerms = turn.focusProducts;
    // What the shopper answered so far, then the question being asked now
    // with its offered answers: lets the shelf judge keep the pages that fit
    // (lib/search/shelf-judge.ts).
    const readTurn = (content: string) => parseAssistantMessage({ message: { id: '', role: 'assistant', content } });
    const pending = pendingTurn(msgs, (content) => {
      const parsed = readTurn(content);
      return { question: parsed.prompt, offered: parsed.suggestedUserAnswers };
    });
    const shelfTurns = [...shopperTurns(msgs, (content) => readTurn(content).prompt), ...(pending ? [pending] : [])];
    const args = {
      currentHref: pathname,
      searchTerm: turn.searchQuery ?? '',
      originalUserQuery: shelfOpening,
      locale: effectiveLocale,
      isDone: doneMode,
      subregionPref: subregionFirstPref,
      focusTerms,
      patient: true,
      shelfTurns,
    };
    return {
      args,
      argsKey: JSON.stringify(args),
      // The locale is part of the key: a language flip must refetch even when
      // the wrong-language pass already produced displayable hits.
      fetchKey: `${turn.searchQuery}|${shelfOpening ?? ''}|${doneMode}|${effectiveLocale.path}|${focusTerms.join('\u001f')}`,
    };
  };

  /**
   * Run a shelf lookup through the stream (lib/shelf-stream.ts): each product
   * the judge picks goes on the shelf the moment it is written, in the judge's
   * order, and stays where it landed (Olivier, 2026-11-01: show each product as
   * soon as it is known, only once). Cards count only for the turn that asked,
   * and not while a focus shelf leads. Resolves with the lookup's whole result,
   * for the end-of-turn fetch; falls back to the server action if the stream
   * route fails, unless a newer lookup cancelled this one.
   */
  const openShelfLookup = (args: ReturnType<typeof shelfLookupFor>['args'], messageId: string): Promise<SearchPagesResult> => {
    shelfStreamRef.current?.controller.abort();
    const controller = new AbortController();
    const owner = { messageId, controller, hits: [] as AlgoliaHit[] };
    shelfStreamRef.current = owner;
    const openedAt = performance.now();
    const timing = (what: string) => {
      if (process.env.NODE_ENV !== 'production') console.info(`[shelf] ${what} +${Math.round(performance.now() - openedAt)} ms`);
    };
    return streamShelfLookup(args, {
      signal: controller.signal,
      onCard: (card) => {
        if (shelfStreamRef.current !== owner) return;
        const current = messagesRef.current;
        if (current[current.length - 1]?.id !== messageId) return;
        if (focusHitsRef.current.length > 0) return;
        if (owner.hits.length === 0) timing('first card');
        owner.hits = addStreamCard(owner.hits, card);
        setAlgoliaMatchs(owner.hits);
      },
    }).then((result) => {
      timing('lookup done');
      return result;
    }).catch((error) => {
      if (controller.signal.aborted) throw error;
      console.warn('[search] shelf stream failed, asking the action:', error);
      return searchPagesWithStatus(args);
    });
  };

  /* -----------------------------------------------------------
   * Effect: Start the shelf lookup while the turn is still streaming
   * --------------------------------------------------------- */
  // Every input of the shelf lookup streams before `suggestedUserAnswers`;
  // after it the turn still streams confirmedElements, searchBrief and budget,
  // which the lookup does not read. Starting it the moment the answers close
  // saves that tail on every turn. The end-of-turn fetch awaits this promise
  // when it would send exactly the same arguments, and asks again otherwise,
  // so the shelf is the one it would have been; what it does with the answer
  // (page creation, auto-open) still waits for the finished turn.
  useEffect(() => {
    if (!isLoading) return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant' || typeof last.content !== 'string') return;
    if (shelfPrefetchRef.current?.messageId === last.id) return;
    if (!closedSuggestedAnswers(last.content)) return;
    const turn = parseAssistantMessage({ message: last });
    if (!turn.searchQuery) return;
    const lookup = shelfLookupFor(messages, turn);
    // The end-of-turn gate: it asks only for a changed query or an empty
    // shelf, never again on a concluded one, never over a scheduled re-run.
    const hasMatch = algoliaMatchs && (Array.isArray(algoliaMatchs) ? algoliaMatchs.length > 0 : true);
    if (lastFetchedQueryRef.current === lookup.fetchKey && hasMatch) return;
    if (turn.isDone && isDoneFinal) return;
    const retry = shelfRetryRef.current;
    if (retry.key === lookup.fetchKey && (retry.timer || retry.gaveUp)) return;
    const promise = openShelfLookup(lookup.args, last.id);
    // Awaited, and its failure handled, by the end-of-turn fetch.
    promise.catch(() => {});
    shelfPrefetchRef.current = { argsKey: lookup.argsKey, messageId: last.id, promise };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, isLoading]);

  /* -----------------------------------------------------------
   * Effect: Start the focus shelf while the turn is still streaming
   * --------------------------------------------------------- */
  // The products a turn is about ("iPad Pro", "iPad Air") stream in seconds
  // before its refined query. Looking them up right then puts their photos and
  // prices into the comparison card, and the products on the shelf, while the
  // rows are still arriving; the fetch at the end of the turn then reads the
  // same, now warm, cache entry (focus-shelf.ts).
  useEffect(() => {
    if (!isLoading) return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant' || typeof last.content !== 'string') return;
    const names = closedFocusProducts(last.content);
    if (names.length === 0) return;
    // This turn's language is not known yet (langCode streams last): search
    // in the one the conversation has already settled on.
    const previousTurn = [...messages].reverse().find((m) => m.role === 'assistant' && m.id !== last.id);
    const settledLang = previousTurn ? parseAssistantMessage({ message: previousTurn }).langCode : undefined;
    const prefetchLocale = resolveTurnLocale(settledLang, locale, initLocale);
    const key = `${last.id}|${prefetchLocale.path}|${names.join('\u001f')}`;
    if (focusPrefetchKeyRef.current === key) return;
    focusPrefetchKeyRef.current = key;
    searchFocusShelf({ names, locale: prefetchLocale, currentHref: pathname })
      .then((hits) => {
        // Only for the turn that asked, and only if the end-of-turn fetch has
        // not already settled the shelf.
        if (hits.length === 0 || focusPrefetchKeyRef.current !== key) return;
        focusHitsRef.current = hits;
        setAlgoliaMatchs(hits);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, isLoading, locale.path, pathname]);

  /* -----------------------------------------------------------
   * Effect: Fetch and Process Search Map When Messages Update
   * --------------------------------------------------------- */
  useEffect(() => {
    const fetchSearchMap = async () => {
      try {
        if (!isLoading && messages.length > 0) {
          // Immediately grab and parse the last message.
          const lastMessage = messages[messages.length - 1];

          // Only process assistant messages. When append() adds a user message there is a
          // brief render window where isLoading is still false; processing a user message
          // here would produce searchQuery=null and incorrectly call endSearch() or reload().
          // The same window exists when reload() removes the last assistant message.
          if (lastMessage.role === 'user') return;

          const lastContent = parseAssistantMessage({ message: lastMessage });
          // Track the AI's per-turn scope decision (undefined only on parse failure,
          // which keeps the previous value instead of downgrading a kit conversation).
          if (lastContent.searchScope) {
            currentSearchScopeRef.current = lastContent.searchScope;
          }
          // Cumulative user-confirmed kit elements; a turn that omits the field
          // keeps the last non-empty set (mid-stream partials must not wipe it).
          if (lastContent.confirmedElements.length > 0) {
            confirmedElementsRef.current = lastContent.confirmedElements;
          }
          // Same cumulative treatment for the brief: it is re-emitted whole each
          // turn, so a turn that omits it (or fails to parse) keeps the last good
          // one rather than dropping the constraints gathered so far.
          if (lastContent.searchBrief) {
            searchBriefRef.current = lastContent.searchBrief;
          }
          // Budget from the COMPLETED turn (this effect only runs when
          // !isLoading — the trailing JSON field is whole, so the "max":
          // 15-of-1500 partial-stream hazard can't bite here). The AI
          // re-expresses the user's own statement, adding currency, the units
          // the number covers and "not a dollar over" — it upgrades a parser
          // capture but never overrides a number typed into the chip, and
          // never brings back one the shopper removed.
          if (lastContent.budget && budgetRef.current?.channel !== 'chip' && !budgetClearedRef.current) {
            const aiBudget = lastContent.budget;
            const previous = budgetRef.current;
            console.log('[budget] AI capture:', aiBudget, 'previous:', previous);
            applyBudget({
              max: aiBudget.max,
              min: aiBudget.min,
              currency: aiBudget.currency ?? previous?.currency ?? getCurrencyForRegion(locale.region),
              source: 'stated',
              raw: previous?.raw,
              quantity: aiBudget.quantity ?? previous?.quantity,
              stretch: aiBudget.stretch ?? previous?.stretch ?? true,
              channel: 'ai',
            });
          }
          let algolia: AlgoliaHit | AlgoliaHit[] | undefined = algoliaMatchs;
          const isDone = lastContent.isDone;
          let searchQuery = lastContent.searchQuery;
          const langCode = lastContent.langCode;

          // The shopper's own words decide what language this whole surface is
          // shown in. `resolveTurnLocale` fails closed: it moves only to a real
          // language of this very region, and "und" (the model's word for a
          // message that is only a brand and a model number) leaves the locale
          // exactly where it is — so a neutral follow-up inside a French
          // conversation never yanks the shopper back to English.
          //
          // This used to additionally require the raw text to carry the target
          // language's FUNCTION words. That vetoed the common case: product
          // queries are mostly content words, so "aspirateur automatique
          // silencieux" scored zero evidence and a French shopper got a French
          // conversation wrapped in an entirely English interface, linked to the
          // English page while the French one already existed. The neutrality
          // judgment that guard was really making now happens in the model,
          // where three words are enough to make it (see resolveTurnLocale).
          //
          // `effectiveLocale` is the flip applied to THIS pass: setLocale only
          // lands next render, and the shelf fetch below runs now. Without it
          // the pass after an English query on fr-CA still searched the French
          // slice — and when those wrong-language hits cleared the display gate
          // (English-slugged fr-CA pages do), the locale-less fetchKey never
          // allowed the refetch, leaving French cards under English labels.
          const effectiveLocale = resolveTurnLocale(langCode, locale, initLocale);
          // Only update locale if it actually changed to prevent effect re-triggering.
          if (effectiveLocale.path !== locale.path) {
            setLocale(effectiveLocale);
          }

          if (!searchQuery) {
            // Discussion turn with a valid prompt but (against instructions) no searchQuery:
            // keep the conversation alive instead of retrying/ending — just skip Algolia this turn.
            if ((lastContent.turnMode ?? 'refine') === 'discussion' && lastContent.prompt) {
              productSearchRetryCountRef.current = 0;
              setProductSearchParseError(false);
              setIsDoneFinal(false);
              return;
            }
            // Check if this is a parse failure (AI returned non-JSON) - retry up to 2 times
            const isAssistantMessage = lastMessage.role === 'assistant';
            const hasContent = typeof lastMessage.content === 'string' && lastMessage.content.trim().length > 0;
            const looksLikeParseFailure = hasContent && !lastMessage.content.trim().startsWith('{');
            const shouldRetry =
              currentSearchIntent === 'product-search' &&
              isAssistantMessage &&
              (looksLikeParseFailure || lastContent.prompt === null);

            if (shouldRetry && productSearchRetryCountRef.current < 2) {
              productSearchRetryCountRef.current += 1;
              setProductSearchParseError(false);
              reload({
                body: {
                  id: STREAM_SEARCH_CHAT_ID,
                  modelId: GEMINI_FLASH_MODEL,
                  locale,
                  productTitles,
                  pageContext: title,
                  shouldUsePageContext: currentClassification?.shouldUsePageContext ?? false,
                  userRegion: userExplicitlyChoseRegion && userRegion ? userRegion : undefined,
                  searchType: currentClassification?.searchType,
                },
              });
              return;
            }
            if (shouldRetry && productSearchRetryCountRef.current >= 2) {
              setProductSearchParseError(true);
            }
            productSearchRetryCountRef.current = 0;
            endSearch();
            return;
          }

          // Valid parse - reset retry state
          productSearchRetryCountRef.current = 0;
          setProductSearchParseError(false);
          // A new turn arrived and parsed, so the stream works again: the
          // banner a failed attempt left up must not sit over a good answer.
          // (A failed LOOKUP for this turn is cleared where the lookup succeeds.)
          if (failedStageRef.current === 'turn' && lastMessage.id !== failedOverTurnIdRef.current) {
            clearSearchFailure();
          }
          networkRetryUsedRef.current = false;

          // NEVER auto-redirect a RESTORED turn: the user is here because they pressed
          // Back on the very page we would send them to. Re-firing the redirect would
          // bounce them straight out and make the back button useless. Fresh turns are
          // not in `restoredTurnIds`, so a continued conversation redirects normally.
          const isRestoredTurn = restoredTurnIds.has(lastMessage.id);

          // Run the (LLM-free, cached) page search whenever the AI's refined query
          // changed since the last fetch, so the live propositions shelf tracks the
          // conversation turn by turn — or when we have no matches yet. The ref key
          // includes isDoneFinal because the done-mode fetch returns a larger set.
          // The shopper's opening words, searched alongside the AI's refined
          // query. `searchQuery` is rewritten every turn under a conciseness
          // budget and can lose an attribute the user actually named, so this
          // keeps the pages they literally asked for in the candidate pool.
          // Read off `messages` rather than a ref so a restored transcript
          // (which rehydrates the same user-first layout) still has it.
          const originalUserQuery = messages.find((m) => m.role === 'user')?.content?.trim() || undefined;
          // ...unless this turn's model judged that a later answer changed WHICH
          // products qualify ("collant pour femme" → "pour ballet"): then the
          // opening words would lift the pages that answer ruled out, so the
          // shelf searches, and rescues on, the refined query alone.
          const shelfOpening = openingWordsForShelf(originalUserQuery, lastContent);

          const hasAlgoliaMatch = algoliaMatchs && (Array.isArray(algoliaMatchs) ? algoliaMatchs.length > 0 : true);
          // Its arguments, and the fetchKey this gate compares (shelfLookupFor).
          const shelfLookup = shelfLookupFor(messages, lastContent);
          const fetchKey = shelfLookup.fetchKey;
          const queryChanged = lastFetchedQueryRef.current !== fetchKey;
          // A restored-concluded turn that came back with no shelf snapshot (e.g. the
          // auto-redirect exit, which never showed one) needs its own done-mode fetch —
          // it wouldn't otherwise qualify above because `isDone` is already true for a
          // concluded transcript. Requiring `queryChanged` keeps this from looping: once
          // the fetch runs, `lastFetchedQueryRef` is set and a second effect pass (e.g.
          // triggered by the resulting setAlgoliaMatchs) sees queryChanged===false even
          // if the fetch came back with nothing displayable.
          const needsRestoredRefetch = isRestoredTurn && !hasAlgoliaMatch && queryChanged;
          // Set when the lookup could not be fully answered but the shopper's
          // own words still found pages: those are shown, and this records what
          // the policy decided, so nothing below mistakes them for the answer.
          let partialShelf: DegradedShelfAction | null = null;
          if (((queryChanged || !hasAlgoliaMatch) && (!isDone || !isDoneFinal)) || needsRestoredRefetch) {
            const shelfRetry = shelfRetryRef.current;
            // A re-run of this very lookup is already scheduled, or its re-runs
            // are spent: do not ask again on every pass of this effect.
            if (shelfRetry.key === fetchKey && (shelfRetry.timer || shelfRetry.gaveUp)) return;
            // The conversation moved on to another lookup; the old one's
            // re-runs go with it.
            if (shelfRetry.key !== fetchKey) clearShelfRetry();

            // This lookup is already being awaited: its streamed cards re-run
            // this effect, and asking again would cancel it.
            const inFlightKey = `${lastMessage.id}|${shelfLookup.argsKey}`;
            if (shelfLookupInFlightRef.current === inFlightKey) return;

            // The lookup started mid-stream for this very turn, when it sent
            // exactly these arguments; otherwise a new one.
            const prefetched = shelfPrefetchRef.current;
            shelfPrefetchRef.current = null;
            const reusable = prefetched?.messageId === lastMessage.id && prefetched.argsKey === shelfLookup.argsKey;

            let outcome: SearchPagesResult | null = null;
            shelfLookupInFlightRef.current = inFlightKey;
            try {
              outcome = await (reusable ? prefetched.promise : openShelfLookup(shelfLookup.args, lastMessage.id));
            } catch (error) {
              // The action itself failed (a dropped connection, a platform
              // error). That is an unanswered lookup too, not an empty one;
              // swallowing it left a concluding turn on "stand by" for good.
              console.warn('[search] shelf lookup failed:', error);
            } finally {
              if (shelfLookupInFlightRef.current === inFlightKey) shelfLookupInFlightRef.current = null;
            }
            // The database could not answer. Never let that flow into the
            // create-a-page branch below; what happens instead depends on
            // whether this turn needs the answer (shelf-degraded-policy.ts).
            if (!outcome || outcome.degraded) {
              // The shopper already answered again: this lookup's turn is no
              // longer the one on screen, and its failure is nobody's problem.
              const current = messagesRef.current;
              if (current[current.length - 1]?.id !== lastMessage.id) return;
              const attempt = shelfRetryRef.current.key === fetchKey ? shelfRetryRef.current.attempt : 0;
              const action = onDegradedShelf({ isDone: !!isDone, attempt });
              shelfRetryRef.current =
                action === 'retry'
                  ? {
                      key: fetchKey,
                      attempt: attempt + 1,
                      gaveUp: false,
                      timer: setTimeout(() => {
                        shelfRetryRef.current.timer = null;
                        setShelfRetryTick((tick) => tick + 1);
                      }, SHELF_LOOKUP_RETRY_DELAY_MS),
                    }
                  : { key: fetchKey, attempt, gaveUp: true, timer: null };
              if (!outcome || outcome.hits.length === 0) {
                if (action === 'fail') failSearchAsDegraded('failed', 'shelf');
                if (action === 'continue') {
                  // The question is on screen and can be answered without a
                  // shelf. Say nothing; the next turn looks again.
                  setAiGeneratedQuery(searchQuery);
                  setIsDoneFinal(false);
                }
                return;
              }
              // The refined query timed out, but the shopper's own words found
              // pages. Show those meanwhile (a scheduled re-run replaces them
              // with the full answer); they are never grounds to create a page.
              partialShelf = action;
            } else {
              clearShelfRetry();
              // The lookup answered: a banner left by an earlier failed one goes.
              if (failedStageRef.current === 'shelf') clearSearchFailure();
              lastFetchedQueryRef.current = fetchKey;
            }
            algolia = outcome.hits;
            untranslatedHitsRef.current = outcome.untranslated;
            focusHitsRef.current = outcome.focus ?? [];
            rankedHitsRef.current = outcome.hits;
            focusPrefetchKeyRef.current = null;
            setShelfSubregionCode(outcome.subregion);
          }

          // Check for perfect exact match when in isDoneFinal state
          // Only redirect if we have a HIGH-QUALITY match (relevance score threshold).
          // When the shelf shows focus pages, `algoliaMatchs` holds those, not
          // the ranked answer to the query: decide on the ranked hits instead.
          const redirectPool: AlgoliaHit | AlgoliaHit[] | undefined =
            focusHitsRef.current.length > 0 ? rankedHitsRef.current : algolia;
          if (isDoneFinal && redirectPool && !isRestoredTurn) {
            const algoliaResults = Array.isArray(redirectPool) ? redirectPool : [redirectPool];

            // Auto-open only when canAutoRedirect says this is THE answer.
            // Close title matches and score-0 typo rescues stay on the shelf.
            const exactMatches = algoliaResults.filter((hit) =>
              isAutoRedirectCandidate(hit, algoliaResults, searchQuery, originalUserQuery),
            );

            // If we have high-quality exact matches, prioritize by locale/language
            if (exactMatches.length > 0) {
              // Priority 1: Exact locale match (e.g., fr-CA when user is on fr-CA)
              let bestMatch = exactMatches.find(hit => hit.lang === locale.path);

              // Priority 2: Same language, different region (e.g., fr-FR when user is on fr-CA)
              if (!bestMatch) {
                bestMatch = exactMatches.find(hit => {
                  if (!hit.lang) return false;
                  const hitLanguage = hit.lang.split('-')[0];
                  return hitLanguage === locale.languageCode;
                });
              }

              // Priority 3: First available match (fallback)
              if (!bestMatch) {
                bestMatch = exactMatches[0];
              }

              if (bestMatch) {
                // Found perfect exact match with locale preference AND high relevance score, redirect immediately
                const redirectUrl = selectLocalizedUrl(bestMatch.href, locale);
                if (redirectUrl && searchQuery) {
                  // Only auto-open when the page matches the user's EFFECTIVE
                  // price tier. A mismatch used to wipe the shelf and force
                  // page creation — that's how "iPhone 17 Pro Max" (whose
                  // default page exists) spent the loading theater
                  // regenerating a duplicate. Keep the matches; "Create a
                  // new page" stays on the shelf for a real tier need.
                  if (hrefMatchesPriceTier(redirectUrl, selectedPriceRange)) {
                    // Save to search history (pg18 info from Algolia hit if available)
                    addToSearchHistory({
                      searchQuery,
                      href: redirectUrl,
                      locale: locale.path,
                      pg18: bestMatch.pg18 || false,
                    });
                    // Finalize persisted chat transcript with the redirect target
                    finalizeTranscript(searchQuery, redirectUrl, snapshotShelfForTranscript());
                    // Don't call cancelSearch() - keep loading state visible until navigation completes
                    // Existing page + active budget → carry the number so the page
                    // re-orders its picks for it. Product pages are price-neutral.
                    const carryBudget =
                      currentClassification?.searchType === 'product' ? null : budgetRef.current;
                    window.location.href = withBudgetQuery(redirectUrl, carryBudget);
                    return;
                  }
                  // Tier mismatch: keep the matches and show the shelf. Product
                  // pages are price-neutral (the classification fast-path already
                  // forces $$). Wiping the shelf and force-creating is how an
                  // exact product whose default page exists — "iPhone 17 Pro Max"
                  // — spent the loading theater regenerating a duplicate.
                  // "Create a new page" stays on the shelf for a real tier need.
                }
              }
            }
          }

          // Determine the high-quality matches we'd actually surface to the user during Q&A.
          // We only want to show results that are genuinely helpful, not weak/partial matches.
          //
          // CRITICAL: every downstream decision (show results vs. create a page, and the
          // setIsDoneFinal early-return below) MUST be based on these displayable matches —
          // NOT on the raw `algolia` results. Otherwise a set of weak (below-threshold)
          // matches flips isDoneFinal=true while algoliaMatchs stays empty, leaving the
          // loading UI stuck forever with no results to render and no page creation.
          const algoliaResultsArray: AlgoliaHit[] = algolia
            ? Array.isArray(algolia)
              ? algolia
              : [algolia]
            : [];
          // Two decisions the old flat 5000 conflated: "is anything here an
          // answer at all" (else create a new page) and "which of these belong
          // on the shelf". A single absolute cut made a broad query like
          // "kayak" — where every hit covers the one word fully — show either
          // everything or nothing.
          // "Do we have an answer" is asked of the BEST-COVERED candidate, not
          // the top-scored one: the score orders and coverage gates, so they
          // need not agree. Measured on real traffic, "balayeuse sans fil" had
          // the exact page at coverage 1.000 while a wireless KEYBOARD page led
          // on score at 0.473 — gating on the leader discarded a perfect match
          // and offered to create a page that already exists.
          const bestCoverage = algoliaResultsArray.reduce(
            (m, hit) => (typeof hit?.coverage === 'number' && hit.coverage > m ? hit.coverage : m),
            -1,
          );
          const shelfFloor =
            bestCoverage >= 0 ? Math.max(SHELF_ABSOLUTE_FLOOR, bestCoverage * SHELF_RELATIVE_RATIO) : 0;
          // The shopper named specific products and the catalogue has some of
          // them: those pages are the shelf, with the named products leading.
          // They skip the coverage gates on purpose — they were chosen by
          // product name, not by how many of the conversation's words a page
          // repeats, which is exactly the measure that ranked stylus pages
          // above both iPads.
          // Otherwise — typo rescue: when nothing clears the display gate but a hit IS the
          // query's own name up to small typos ("iphne 17 pro max"), show that
          // hit instead of offering to create a duplicate page. Tested against
          // both the AI's refined query and the shopper's raw words — the typo
          // lives in whichever the AI didn't rewrite. Shelf-only: auto-open
          // still requires real coverage via canAutoRedirect. The raw words
          // are the ones the shelf searched (`shelfOpening`): none once the
          // model judged a later answer changed which products qualify.
          // Pages the shelf judge picked (lib/search/shelf-judge.ts): it read
          // them against everything the shopper said, so they are shown as
          // picked and the word-coverage gates below do not second-guess it.
          const judgedMatches = [...algoliaResultsArray, ...untranslatedHitsRef.current].filter((hit) => hit.judged);
          const displayMatches = focusHitsRef.current.length > 0
            ? focusHitsRef.current
            : judgedMatches.length > 0
            ? judgedMatches
            : !algoliaResultsArray.some((hit) => passesDisplayGate(hit))
            ? algoliaResultsArray
                .filter(
                  (hit) =>
                    isNearExactSubjectMatch(searchQuery, hit.title) ||
                    isNearExactSubjectMatch(shelfOpening, hit.title) ||
                    passesNearMissRescue(hit, searchQuery) ||
                    passesNearMissRescue(hit, shelfOpening),
                )
                .slice(0, 3)
            : [
                ...algoliaResultsArray.filter((hit) => passesShelfFloor(hit, shelfFloor)),
                // Previews go last: a French shelf always leads with French.
                ...untranslatedHitsRef.current.filter((hit) => passesShelfFloor(hit, shelfFloor)),
              ];
          const hasDisplayableMatches = displayMatches.length > 0;
          // A concluding turn with nothing to show goes to page creation below,
          // which has its own state; only a conversation that carries on
          // leaves the slot waiting on its next answer.
          // (A partial lookup settles nothing: it cannot say no page fits.)
          setShelfSettledEmpty(!hasDisplayableMatches && !isDone && !partialShelf);

          // Sync algoliaMatchs state with the displayable matches (only when it changes, to
          // avoid infinite effect re-runs).
          if (hasDisplayableMatches) {
            const currentMatches = algoliaMatchs;
            const currentArray = currentMatches
              ? Array.isArray(currentMatches)
                ? currentMatches
                : [currentMatches]
              : [];
            const isDifferent =
              currentArray.length !== displayMatches.length ||
              currentArray.some(
                (match, idx) =>
                  match.id !== displayMatches[idx]?.id ||
                  // Same page can arrive with fresh product previews (e.g. after the
                  // done-mode refetch) — presence/count is a cheap change signal.
                  (match.products?.length ?? 0) !== (displayMatches[idx]?.products?.length ?? 0),
              );
            if (isDifferent) {
              setAlgoliaMatchs(displayMatches);
            }
          } else if (algoliaMatchs) {
            // No displayable matches now — clear any stale ones.
            setAlgoliaMatchs(undefined);
          }

          // Immediately update UI state
          setAiGeneratedQuery(searchQuery); // Update AI query display, not search input

          // Save all searches to history (even if not done)
          // We'll update the href when the search completes
          //
          // Skip for a restored turn: the transcript already has its real
          // finalHref from before, and this would otherwise overwrite it with
          // `pathname` — the very search page the user is looking at.
          if (searchQuery && !isRestoredTurn) {
            const currentUrl = pathname;
            addToSearchHistory({
              searchQuery,
              href: currentUrl, // Temporary URL, will be updated when search completes
              locale: locale.path,
              pg18: isAdultContent, // Will be updated if we detect adult content later
            });
          }

          // Continue with further processing if the process is done.
          if (!isDone) {
            setIsDoneFinal(false);
            return;
          }

          // Offload heavy asynchronous work so it doesn't block the main thread.
          setTimeout(async () => {
            updateUrlWithQuery(searchQuery, locale);

            // If we have GOOD (displayable) Algolia results, complete the search and show
            // them to the user. We gate on `hasDisplayableMatches` (not raw `algolia`) so that
            // a set of weak/below-threshold matches can never flip isDoneFinal=true while
            // algoliaMatchs is empty — which previously left the loading UI stuck forever.
            if (hasDisplayableMatches && !isBypassingAlgolia.current) {
              setIsDoneFinal(true);
              // Concluding on pages from a partial lookup: they are what the
              // shopper gets, so the re-run that was scheduled is dropped.
              if (partialShelf) {
                if (shelfRetryRef.current.timer) clearTimeout(shelfRetryRef.current.timer);
                shelfRetryRef.current = { ...shelfRetryRef.current, gaveUp: true, timer: null };
                if (failedStageRef.current === 'shelf') clearSearchFailure();
              }
              // DON'T set isSearching to false - keep the live results shelf visible
              // IMPORTANT: Return here and wait for user to either:
              // 1. Click a product card / page link on the shelf (link navigation)
              // 2. Click "Create a new page" (calls handleCreatePageFromShelf)
              // Do NOT automatically start page creation - that would cause premature loading
              return;
            }

            // A restored turn is the shopper coming BACK to a finished search.
            // Creating a page from an empty refetch would duplicate the page
            // they just left (and is why the loading theater replaced the shelf
            // on `?q=` resumes). Leave the concluded UI; the refetch above is
            // the only work this turn should do.
            if (isRestoredTurn) {
              setIsDoneFinal(true);
              return;
            }

            // The lookup was not answered and what it did find is not worth
            // showing. That is not "no page exists": wait for the scheduled
            // re-run, or fail once the re-runs are spent. Never create.
            if (partialShelf) {
              if (partialShelf === 'fail') failSearchAsDegraded('failed', 'shelf');
              return;
            }

            // No displayable Algolia results: create the page.
            // Pass the pre-validated searchType from classification to skip validateSearch.
            await executeSearchMapCreation(
              searchQuery,
              false,
              currentClassification?.searchType,
              undefined,
              undefined,
              toBudgetSpec(budgetRef.current),
            );
          }, 0); // Offload heavy async processing to the event loop
        } else {
          setAlgoliaMatchs(undefined);
        }
      } catch (error) {
        // Error in fetchSearchMap
      }
    };

    if (!isLoading) {
      fetchSearchMap();
    }
    // Note: setSearchInput and router are stable and don't need to be in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, locale, algoliaMatchs, isDoneFinal, messages, shelfRetryTick]);

  /* -----------------------------------------------------------
   * Effect: Retry a turn that failed offline once the connection returns
   * --------------------------------------------------------- */
  useEffect(() => {
    const onOnline = () => {
      if (failedOfflineRef.current && failedStageRef.current === 'turn') retryHandlerRef.current();
    };
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('online', onOnline);
      clearShelfRetry();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* -----------------------------------------------------------
   * Effect: Clear lastProcessedInputRef when user initiates a new search (enterPressed)
   * So re-searching the same query triggers handleNewSearch again
   * --------------------------------------------------------- */
  useEffect(() => {
    if (enterPressed) {
      lastProcessedInputRef.current = null;
    }
  }, [enterPressed]);

  /* -----------------------------------------------------------
   * Effect: Trigger New Search When Input Prop Updates (e.g. via suggestion click)
   * Guards added to prevent infinite loops:
   * - Don't trigger if already loading or executing parallel search
   * - Don't trigger if this input was already processed
   * --------------------------------------------------------- */
  // Attachments sent with no words (a pasted link on its own): the input
  // effect below cannot fire for "", so the bar bumps this tick instead.
  useEffect(() => {
    if (attachmentSubmitTick === 0) return;
    if (attachmentsRef.current.length === 0 || isLoading || isParallelExecuting) {
      setEnterPressed(false);
      return;
    }
    lastProcessedInputRef.current = null;
    void handleNewSearch('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachmentSubmitTick]);

  useEffect(() => {
    // Reset ref when input is cleared so the same value (e.g. SKIP) can trigger again on next set
    if (!input) {
      lastProcessedInputRef.current = null;
      return;
    }
    if (!isLoading && !isParallelExecuting && lastProcessedInputRef.current !== input) {
      lastProcessedInputRef.current = input;
      handleNewSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  /* -----------------------------------------------------------
   * Effect: Show region selector after user answers first question only when search needs region
   * (e.g. water softener, modem); generic queries (e.g. laptop, high pad) skip the selector.
   * --------------------------------------------------------- */
  useEffect(() => {
    // Trigger when user has answered the first question
    // messages.length === 3 means: [0]=user query, [1]=AI Q1, [2]=user answer to Q1
    // CRITICAL: We don't check !isLoading because when messages.length becomes 3,
    // isLoading is still true (API is processing the answer). We need to show the
    // region selector immediately and stop() the ongoing request.

    // Only show region selector when check-region-needed API said this search needs region.
    // If regionCheckResult is still null (API not yet returned), do not show selector (Q&A continues).
    if (currentSearchIntent === 'product-search' &&
      regionCheckResult?.needsRegion === true &&
      messages.length >= 3 && // User answered Q1 (API may return after Q2 started)
      messages[2]?.role === 'user' && // Verify it's a user message
      !showRegionSelector &&
      !regionSelectionCompleted) { // Don't show again if user already selected

      // Stop the ongoing API request to prevent Q2 from streaming in
      stop();

      // Show region selector BEFORE Q2 appears
      setNeedsRegionSelection(true);
      setShowRegionSelector(true);
      setIsSearching(false); // Pause to wait for region selection
    }
  }, [currentSearchIntent, regionCheckResult, messages.length, messages, showRegionSelector, regionSelectionCompleted, stop]);

  /* -----------------------------------------------------------
   * Effect: Auto-focus input when search bar opens (topMenu mode)
   * --------------------------------------------------------- */
  useEffect(() => {
    // Always-visible product-page bar must not steal focus on load.
    // The mobile bottom sheet still focuses — the shopper just opened it.
    if (embedded) return;
    if (topMenu && inputRef.current) {
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [topMenu, embedded]);

  // Pre-buy morph handoff: the buy-button's "yes" dispatches this event with the
  // exact product being bought. Seed the conversation (rides the useChat body →
  // kit scope + pinned element 0) and auto-submit via the same enterPressed
  // mechanism as initialQuery/autoSearch. A late mount (mobile bottom sheet)
  // also consumes the stash PrebuyMorph left on window if the event already fired.
  //
  // A seed always opens a NEW conversation: whatever this bar was in the middle
  // of is reset, and the seeded question waits until that reset has landed and
  // any turn it stopped has wound down. Sent sooner, it would be taken as an
  // answer to the old conversation, or refused as a second search in flight.
  const [queuedSeedMessage, setQueuedSeedMessage] = useState<string | null>(null);
  const takeSeedRef = useRef<(seed: KitSeedProduct) => void>(() => {});
  takeSeedRef.current = (seed) => {
    resetAllStates();
    setSeedProduct(seed);
    const seedMessage = `I'm buying the ${seed.title} — what else do I need to go with it?`;
    setSearchInput(seedMessage);
    // Cleared so the queued submit below is always a change the input effect sees.
    setInput('');
    setEnterPressed(true);
    setQueuedSeedMessage(seedMessage);
    setRevealConversationTick((tick) => tick + 1);
  };
  useEffect(() => {
    if (!queuedSeedMessage) return;
    if (isLoading || isPageLoading || isParallelExecuting || isClassifyingIntent) return;
    if (messages.length > 0 || pageMessages.length > 0) return;
    setQueuedSeedMessage(null);
    lastProcessedInputRef.current = null;
    setInput(queuedSeedMessage);
  }, [queuedSeedMessage, isLoading, isPageLoading, isParallelExecuting, isClassifyingIntent, messages.length, pageMessages.length, setInput]);
  useEffect(() => {
    const applySeed = (seed?: KitSeedProduct) => {
      if (!seed?.title || !seed?.ASIN) return;
      // Only a bar that is laid out may take the seed. The top-menu cell is
      // display:none below `sm`; taking it there runs the conversation out of
      // sight and spends the stash the bottom sheet's bar reads when it mounts.
      if (!inputRef.current?.getClientRects().length) return;
      delete (window as unknown as Record<string, unknown>).__iaiPendingSeed;
      takeSeedRef.current(seed);
    };
    const onSeededSearch = (e: Event) => {
      // Another bar on the page already took this seed.
      if (!(window as unknown as Record<string, unknown>).__iaiPendingSeed) return;
      applySeed((e as CustomEvent).detail?.seedProduct as KitSeedProduct | undefined);
    };
    const pending = (window as unknown as Record<string, unknown>).__iaiPendingSeed as
      | { seed: KitSeedProduct; ts: number }
      | undefined;
    if (pending && Date.now() - pending.ts < 15_000) {
      applySeed(pending.seed);
    }
    window.addEventListener('iai:open-seeded-search', onSeededSearch);
    return () => window.removeEventListener('iai:open-seeded-search', onSeededSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* -----------------------------------------------------------
   * Effect: `?attach=<url>` / `?note=<text>` — something shared into the site.
   * The native apps' share target (a link from Safari or Amazon, a note from
   * Notes) and any "share to Inception" link land here: the link or note is
   * attached to the main bar, nothing is sent, and the parameters leave the
   * address bar so a reload does not attach them twice. Read on the client,
   * so the search landing stays a cached page.
   * --------------------------------------------------------- */
  const sharedAttachmentsTakenRef = useRef(false);
  useEffect(() => {
    if (topMenu || header || sharedAttachmentsTakenRef.current) return;
    const url = new URL(window.location.href);
    const sharedLink = url.searchParams.get('attach');
    const sharedNote = url.searchParams.get('note');
    if (!sharedLink && !sharedNote) return;
    sharedAttachmentsTakenRef.current = true;
    if (sharedLink) addLinkAttachment(sharedLink);
    if (sharedNote) addNoteAttachment(sharedNote);
    url.searchParams.delete('attach');
    url.searchParams.delete('note');
    window.history.replaceState(window.history.state, '', url.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* -----------------------------------------------------------
   * Effect: `?q=…&autoSearch=true` — run initialQuery for a human tab.
   * Arrives from the extension, from other stores' search links (the
   * middleware turns /s?k=… into one) and from the SearchAction we publish.
   * Same client gate as 404 recovery below (lib/not-found/auto-search-gate:
   * WebDriver, background tab, Back, reload, once per session per URL), and
   * the flag leaves the address bar whatever the gate decides.
   * --------------------------------------------------------- */
  useEffect(() => {
    if (!autoSearch || !initialQuery?.trim()) return;
    if (initialQueryProcessedRef.current === initialQuery) return;
    const key = autoSearchKey(window.location);
    const url = new URL(window.location.href);
    if (url.searchParams.has('autoSearch')) {
      url.searchParams.delete('autoSearch');
      window.history.replaceState(null, '', url.toString());
    }
    return startGatedAutoSearch(key, () => {
      if (initialQueryProcessedRef.current === initialQuery) return;
      initialQueryProcessedRef.current = initialQuery;
      setSearchInput(initialQuery);
      setInput(initialQuery);
      setEnterPressed(true);
    });
  }, [initialQuery, autoSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  /* -----------------------------------------------------------
   * Effect: 404 recovery — submit the prefilled query for a human tab.
   * Same submit mechanism and the same gate as autoSearch above
   * (setInput → handleNewSearch, enterPressed → composer state), but the URL
   * is left alone: the run is remembered per session and URL, so Back onto
   * the dead page never re-fires it.
   * --------------------------------------------------------- */
  useEffect(() => {
    if (!submitPrefill || !prefillQuery?.trim()) return;
    if (initialQueryProcessedRef.current === prefillQuery) return;
    return startGatedAutoSearch(autoSearchKey(window.location), () => {
      if (initialQueryProcessedRef.current === prefillQuery) return;
      initialQueryProcessedRef.current = prefillQuery;
      setSearchInput(prefillQuery);
      setInput(prefillQuery);
      setEnterPressed(true);
    });
  }, [submitPrefill, prefillQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  /* -----------------------------------------------------------
   * Event Handlers
   * --------------------------------------------------------- */

  // Handle region selection confirmation
  const handleRegionConfirm = (location: UserLocation) => {
    setUserRegion(location.region);
    // Mark that user explicitly chose a region (even if they chose "All regions")
    // "All regions" means region is set to "All regions" string, which we handle separately
    setUserExplicitlyChoseRegion(location.region !== 'All regions');
    setShowRegionSelector(false);
    setNeedsRegionSelection(false);
    setRegionSelectionCompleted(true); // Mark as completed to prevent showing again

    // Reset input tracking ref so the same input (e.g., SKIP) can be processed again
    lastProcessedInputRef.current = null;

    // Check if we already have messages (conversation has started)
    if (messages.length >= 3) {
      // User has answered Q1, we're between Q1 and Q2
      // Resume the conversation by reloading with the user's answer and region context
      setIsSearching(true);

      // Reload the conversation with region context
      // This will re-process the user's answer (messages[2]) with region info
      reload({
        body: {
          locale,
          isDemo,
          productTitles,
          pageContext: title,
          shouldUsePageContext: currentClassification?.shouldUsePageContext ?? false,
          userRegion: location.region !== 'All regions' ? location.region : null,
          searchType: currentClassification?.searchType,
        }
      });

      return;
    }

    // Fallback: if somehow we have fewer messages, handle the old flow
    if (pendingSearchInput) {
      const storedInput = pendingSearchInput;
      setPendingSearchInput(null);

      // CRITICAL: Set intent and isSearching BEFORE calling setInput to ensure container stays visible
      setCurrentSearchIntent('product-search');
      setIsSearching(true);
      setAlgoliaMatchs(undefined);
      setSearchInput('');

      // Small delay to ensure state updates are processed before triggering the search
      setTimeout(() => {
        // Build the initial messages array
        let initialMessages: Message[] = [
          { role: 'user', content: storedInput, id: 'user-search' }
        ];

        // If specific region selected, add indication message
        if (location.region !== 'All regions') {
          const searchContent = getSearchUiContent(locale);
          const regionIndication = searchContent.regionSelector.regionIndicationTemplate
            .replace('{city}', location.city)
            .replace('{region}', getLocalizedRegionName(locale.regionCode, location.region, locale.languageCode));

          initialMessages.push(
            { role: 'assistant', content: regionIndication, id: 'region-indication' }
          );
        }

        setMessages(initialMessages);
        setInput('');

        setTimeout(() => {
          reload({
            body: {
              locale,
              isDemo,
              productTitles,
              pageContext: title,
              shouldUsePageContext: currentClassification?.shouldUsePageContext ?? false,
              userRegion: location.region !== 'All regions' ? location.region : null,
              searchType: currentClassification?.searchType,
            }
          });
        }, 50);
      }, 0);
    }
  };

  // Handle region selection skip
  const handleRegionSkip = () => {
    setShowRegionSelector(false);
    setNeedsRegionSelection(false);

    // Explicitly set userRegion to null when skipping
    setUserRegion(null);
    // User did not choose a region, so don't append it to query
    setUserExplicitlyChoseRegion(false);
    setRegionSelectionCompleted(true); // Mark as completed to prevent showing again

    // Reset input tracking ref so the same input (e.g., SKIP) can be processed again
    lastProcessedInputRef.current = null;

    // Check if we already have messages (conversation has started)
    if (messages.length >= 3) {
      // User has answered Q1, we're between Q1 and Q2
      // Resume the conversation without region context
      setIsSearching(true);

      // Reload the conversation without region context
      reload({
        body: {
          locale,
          isDemo,
          productTitles,
          pageContext: title,
          shouldUsePageContext: currentClassification?.shouldUsePageContext ?? false,
          userRegion: null,
          searchType: currentClassification?.searchType,
        }
      });

      return;
    }

    // Fallback: if somehow we have fewer messages, handle the old flow
    if (pendingSearchInput) {
      const storedInput = pendingSearchInput;
      setPendingSearchInput(null);

      // CRITICAL: Set intent and isSearching BEFORE calling setInput to ensure container stays visible
      setCurrentSearchIntent('product-search');
      setIsSearching(true);
      setAlgoliaMatchs(undefined);
      setSearchInput('');

      // Small delay to ensure state updates are processed before triggering the search
      setTimeout(() => {
        setInput(storedInput);
        handleSubmit(undefined, {
          body: {
            locale,
            isDemo,
            productTitles,
            pageContext: title,
            shouldUsePageContext: currentClassification?.shouldUsePageContext ?? false,
            userRegion: null,
            searchType: currentClassification?.searchType,
          }
        });
      }, 0);
    }
  };

  // A number typed into the budget chip: authoritative (channel 'chip', never
  // overwritten by the parser or the AI). Null empties the chip and keeps the
  // number the conversation heard earlier from coming back. It applies to the
  // next turn and to the page this search builds; nothing re-runs.
  const handleBudgetCommit = (max: number | null) => {
    if (max === null) {
      budgetClearedRef.current = true;
      applyBudget(null);
      return;
    }
    budgetClearedRef.current = false;
    const previous = budgetRef.current;
    applyBudget({
      max,
      currency: previous?.currency ?? getCurrencyForRegion(locale.region),
      source: 'ui',
      quantity: previous?.quantity,
      stretch: previous?.stretch ?? true,
      channel: 'chip',
    });
  };

  // Close the region selector when user focuses on search (e.g. opened via pills)
  // Preserves previous selection when reopened via pill; otherwise skips
  const handleRegionFocusDismiss = () => {
    if (userRegion) {
      setShowRegionSelector(false);
      setRegionSelectionCompleted(true);
    } else {
      handleRegionSkip();
    }
  };
  // Parallel execution strategy - fire classification then use appropriate chat
  /**
   * Cheap primary-key lookup for "does this exact product page already exist?",
   * and navigate to it if so. Returns true when it has taken over navigation.
   *
   * Extracted so BOTH entry points can use it. The fresh-classification path
   * always ran it; the intent-CACHE-hit path below did not, and that asymmetry
   * was a live bug: the cache key is the raw query text, so the SECOND search
   * of the same words in one document (exactly the state a `?q=` URL marks)
   * short-circuited straight to handleDirectProductSearch and never asked
   * whether the page existed. Reported from prod: "iPhone 17 Pro Max" matched
   * on a clean load and fell into page CREATION when repeated on a URL that
   * already carried ?q=iPhone+17+Pro+Max.
   */
  const tryInstantExistingPageRedirect = async (
    userInput: string,
  ): Promise<'redirected' | 'miss' | 'degraded'> => {
    if (isBypassingAlgolia.current) return 'miss';
    setIsCheckingAlgoliaForRedirect(true);
    // A thrown action (a dropped connection) is an unanswered lookup too.
    const lookUp = () =>
      findExistingProductPage({ searchTerm: userInput, locale, patient: true }).catch(() => null);
    try {
      let earlyExisting = await lookUp();
      if (!earlyExisting || earlyExisting.degraded) {
        // Ask once more before giving up: a lookup that timed out on a cold
        // database usually answers on the repeat. This is no longer the
        // instant path, so the loading state may show while it runs.
        setIsCheckingAlgoliaForRedirect(false);
        earlyExisting = await lookUp();
      }
      // A lookup that could not be answered is NOT evidence the page is
      // absent. Reported separately so no caller reads it as "go ahead and
      // create one" — that conflation is how a slow database talked the app
      // into regenerating pages that already existed.
      if (!earlyExisting || earlyExisting.degraded) {
        setIsCheckingAlgoliaForRedirect(false);
        return 'degraded';
      }
      if (earlyExisting.row) {
        const earlyUrl = selectLocalizedUrl(earlyExisting.row.href, locale);
        if (earlyUrl && earlyUrl !== pathname) {
          addToSearchHistory({
            searchQuery: userInput,
            href: earlyUrl,
            locale: locale.path,
            pg18: earlyExisting.row.pg18 || false,
          });
          window.location.href = earlyUrl;
          return 'redirected';
        }
      }
    } catch {
      setIsCheckingAlgoliaForRedirect(false);
      return 'degraded';
    }
    setIsCheckingAlgoliaForRedirect(false);
    return 'miss';
  };

  const executeParallelSearch = async (userInput: string) => {
    lastUserInputRef.current = userInput;
    // A turn that failed left its unanswered message at the end of the
    // conversation (useChat keeps it). Read before the flags below are cleared.
    const trailing = messages[messages.length - 1];
    const replacesFailedTurn = failedStageRef.current === 'turn' && trailing?.role === 'user';
    // A new submit is a new attempt. A previous failure's message and error
    // banner must not hang over it (failSearchAsDegraded hands the words back
    // precisely so the shopper can resubmit).
    setSearchDegraded(false);
    setSearchRejectedMessage(null);
    failedStageRef.current = null;
    failedOfflineRef.current = false;
    networkRetryUsedRef.current = false;
    clearShelfRetry();

    // The turn that failed was the opening query itself, and the shopper is
    // submitting again: this is a first search, not an answer to append. Drop
    // the unanswered message and come back through the input effect once React
    // has committed the empty chat (same bounce as a concluded conversation,
    // below). Before the tracking call, so one search is counted once.
    if (replacesFailedTurn && messages.length === 1) {
      beginFreshProductSearch();
      lastProcessedInputRef.current = null;
      setInput('');
      // Words come back through the input effect; attachments alone through
      // the attachment tick (the input effect never fires for "").
      setTimeout(() => (userInput ? setInput(userInput) : setAttachmentSubmitTick((tick) => tick + 1)), 0);
      return;
    }

    // First-party traffic event — single funnel for every user-initiated search.
    const sentAttachments = attachmentKinds(attachmentsRef.current);
    track('search', {
      query: userInput.slice(0, 256),
      mode: isVoiceSearch ? 'voice' : 'main',
      ...(sentAttachments.length > 0 ? { attachments: sentAttachments.join(',') } : {}),
    });

    // Deterministic budget capture — instant, zero-cost, before any AI token.
    // Runs on EVERY submit (initial query, typed answers, tapped chips, voice),
    // so "moins de 1500$" is heard the moment it's stated. A number typed into
    // the chip is authoritative and never overwritten by re-parsing later
    // messages; a newly stated number brings back a budget the shopper emptied.
    const parsedBudget = userInput
      ? parseBudgetFromText(userInput, { localeCurrency: getCurrencyForRegion(locale.region) })
      : null;
    if (parsedBudget && budgetRef.current?.channel !== 'chip') {
      console.log('[budget] parser capture:', parsedBudget);
      budgetClearedRef.current = false;
      applyBudget({
        ...parsedBudget,
        source: 'stated',
        quantity: budgetRef.current?.quantity,
        stretch: budgetRef.current?.stretch ?? true,
        channel: 'parser',
      });
    }

    // PRIORITY 1: If we're already in an active product-search conversation, continue that conversation
    if (messages.length > 0 && currentSearchIntent === 'product-search') {
      // …unless that conversation has already CONCLUDED. Once a turn reports
      // isDone the shopper is looking at a finished result (shelf/answer), so
      // the next thing they type is a NEW search, not an answer to a pending
      // question.
      //
      // Reported from prod: a concluded search left the URL at
      // `?q=iPhone+17+Pro+Max` (that param is written only on an isDone turn);
      // typing the same query and pressing Enter appended it as
      // "[User's answer …]" and the loading theater ran while a page that
      // already existed was regenerated. Instant-redirecting skipped the
      // shelf. Reloading without `q=` (or clicking the history chip) showed
      // the results because both start a first search / restore, not an
      // answer-append. Mid-Q&A ("calm lakes or ocean?") the typed text IS
      // an answer — that path is unchanged.
      if (isDoneFinal) {
        beginFreshProductSearch();
        // `setMessages([])` is async; handleSubmit in this tick would still
        // append onto the old transcript. Bounce through the input effect
        // after React has committed the empty chat (same pattern as SKIP);
        // attachments alone bounce through the attachment tick.
        lastProcessedInputRef.current = null;
        setInput('');
        setTimeout(() => (userInput ? setInput(userInput) : setAttachmentSubmitTick((tick) => tick + 1)), 0);
        return;
      }
      // The shopper is resubmitting after an answer's turn failed. These words
      // REPLACE the unanswered message; appended after it, the model would be
      // sent the same answer twice.
      if (replacesFailedTurn) setMessages(messages.slice(0, -1));
      setIsParallelExecuting(true);
      try {
        await handleDirectProductSearch(userInput);
      } finally {
        setIsParallelExecuting(false);
      }
      return;
    }

    // MERGED ROUTING (primary search bar, non-topMenu, non-demo):
    // Skip the separate blocking classify-intent (and resolve-product) round-trip.
    // On this surface classify-intent only ever returns intent='product-search', so its
    // sole job was searchType routing — which the streaming discussion model now decides
    // itself on its first turn (specific product → isDone+canonical name, handled by the
    // existing isDone→fetchSearchMap machinery; alternatives → one question; category/kit
    // → Q&A). This collapses two sequential round-trips into one.
    if (!topMenu && !(isDemo && demoType)) {
      setCurrentSearchIntent('product-search');
      setCurrentClassification(null);
      setIsSearching(true);
      setAlgoliaMatchs(undefined);
      setSearchInput('');
      setAiGeneratedQuery(userInput);

      // Background work the create/redirect path needs later. Starts only once
      // the product-search turn below has cleared the bot gate.
      deferSearchHelpers(userInput);

      const mergedRoutingBody = {
        locale,
        isDemo,
        productTitles,
        pageContext: title,
        shouldUsePageContext: false,
        userRegion: chatUserRegion,
      };
      if (attachmentsRef.current.length > 0) {
        await sendMessageWithAttachments(userInput, attachmentsRef.current, mergedRoutingBody);
      } else {
        setInput(userInput);
        handleSubmit(undefined, { body: mergedRoutingBody });
      }
      return;
    }

    // PRIORITY 2: For page questions, we always reclassify the intent on every new search
    // PRIORITY 3: Classification runs for all new searches, regardless of topMenu value

    const cacheKey = `${userInput}_${productTitles?.join(',') || ''}_${viewingHint?.title ?? ''}`;

    // Check cache first for instant classification
    if (intentCache.has(cacheKey)) {
      const cachedClassification = intentCache.get(cacheKey)!;
      setCurrentSearchIntent(cachedClassification.intent);
      setCurrentClassification(cachedClassification);

      // If we have cached intent, just do the specific request
      if (cachedClassification.intent === 'product-search') {
        deferSearchHelpers(userInput);
        // Same existence check the fresh-classification path runs. Without it a
        // repeated exact-product query is appended to the open conversation as
        // an ANSWER ("[User's answer …]" in handleDirectProductSearch) and the
        // shopper is offered page creation for a page that already exists.
        if (cachedClassification.searchType === 'product') {
          applyBudget(null);
          const outcome = await tryInstantExistingPageRedirect(userInput);
          if (outcome === 'redirected') return;
          if (outcome === 'degraded') return failSearchAsDegraded();
        }
        return handleDirectProductSearch(userInput);
      } else {
        return handleDirectPageQuestion(userInput);
      }
    }

    setIsParallelExecuting(true);
    setSearchRejectedMessage(null);

    // Start loading state immediately when classification begins
    setIsSearching(true);
    setIsClassifyingIntent(true);
    // Keep searchInput visible during classification for better UX
    // It will be cleared when the actual search starts
    setAiGeneratedQuery(userInput);

    try {
      let classification: any;

      // Skip classification for demos - set it directly based on demo type
      if (isDemo && demoType) {
        // Set classification directly without API call
        classification = {
          intent: 'product-search',
          confidence: 1.0,
          reason: `Demo mode: ${demoType} search`,
          shouldUsePageContext: false,
          searchType: demoType,
        };

        // Cache the classification
        setIntentCache((prev) => new Map(prev.set(cacheKey, classification)));

        // Clear the classifying intent flag immediately since we skipped the API call
        setIsClassifyingIntent(false);
      } else {
        // Start classification
        // When topMenu is false/undefined, use simplified prompt (searchTypeOnly mode)
        const useSearchTypeOnly = !topMenu;
        const classificationPromise = classifySearchIntentOnly(userInput, cacheKey, useSearchTypeOnly);

        // Store the search input for later handling (used after region selection)
        setPendingSearchInput(userInput);

        // Wait for classification
        classification = await classificationPromise;

        // Classification complete - clear the classifying intent flag
        setIsClassifyingIntent(false);
      }

      // Set the detected intent and full classification
      setCurrentSearchIntent(classification.intent);
      setCurrentClassification(classification);

      // Now use the appropriate chat hook based on classification
      if (classification.intent === 'product-search') {
        // Handle error searchType - reject invalid queries with user feedback
        if (classification.searchType === 'error') {
          setSearchRejectedMessage(getSearchUiContent(locale).searchRejectedMessage);
          setIsSearching(false);
          setIsClassifyingIntent(false);
          return;
        }

        // INSTANT REDIRECT: for product-classified queries, do the cheap PK lookup NOW —
        // before the resolver and region check. A hit (common for exact
        // brand+model queries like "iPhone 17 Pro Max") redirects in classify_time + ~100ms.
        // On a miss we fall through and the resolver / fuzzy search run as normal.
        if (classification.searchType === 'product') {
          applyBudget(null);
          const outcome = await tryInstantExistingPageRedirect(userInput);
          if (outcome === 'redirected') return;
          if (outcome === 'degraded') return failSearchAsDegraded();
        }

        // Context-aware approximate→product resolution (runs on Enter, gated by the cheap
        // classify-intent flag so it never adds latency to obvious category/kit queries).
        // When the query loosely points at ONE specific product ("those sony headphones
        // that block plane noise"), the Claude+web resolver returns the canonical name
        // ("Sony WH-1000XM5"); we route the product fast-path on THAT name so the user lands
        // on the product page instead of a generic category. No hard-coded list.
        if (classification.mightBeSpecificProduct) {
          try {
            const resp = await fetch('/api/resolve-product', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ query: userInput, locale }),
            });
            // The resolver streams newline-delimited JSON: a "browsing" status when it
            // invokes web search, then a final "result". Read it so the UI can show the
            // localized browsing line the moment the model starts searching.
            type ResolverResult = { resolvedProduct?: string | null; resolveConfidence?: string };
            let resolved: ResolverResult | null = null;
            if (resp.body) {
              const reader = resp.body.getReader();
              const decoder = new TextDecoder();
              let buffer = '';
              for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                let nl = buffer.indexOf('\n');
                while (nl >= 0) {
                  const line = buffer.slice(0, nl).trim();
                  buffer = buffer.slice(nl + 1);
                  nl = buffer.indexOf('\n');
                  if (!line) continue;
                  let evt: { type?: string; status?: string; result?: ResolverResult | null } | null = null;
                  try { evt = JSON.parse(line); } catch { continue; }
                  if (evt?.type === 'status' && evt.status === 'browsing') {
                    setResolverBrowsing(true);
                  } else if (evt?.type === 'result') {
                    resolved = evt.result ?? null;
                  }
                }
              }
            }
            if (
              resolved?.resolvedProduct &&
              (resolved.resolveConfidence === 'high' || resolved.resolveConfidence === 'medium')
            ) {
              // eslint-disable-next-line no-param-reassign
              userInput = resolved.resolvedProduct;
              classification.searchType = 'product';
              setAiGeneratedQuery(userInput);
            }
          } catch {
            // Resolver failure → keep the original classification (no behavior change).
          } finally {
            setResolverBrowsing(false);
          }
        }

        // A 'product' search never streams a product-search turn and skips the
        // region check, whose answer only the Q&A reads. Every other search
        // type parks it until its first product-search turn clears the gate.
        if (classification.searchType === 'product') {
          searchHelperGate.cancel();
        } else {
          deferSearchHelpers(userInput);
        }

        // If searchType is 'product' (specific brand+model), skip Q&A entirely
        // Behave as if Q&A completed with isDone: true
        if (classification.searchType === 'product') {
          applyBudget(null);
          setAlgoliaMatchs(undefined);
          // Suppress the loading UI while we silently check Algolia — if we're going to redirect
          // the user should never see a loading screen flash.
          setIsCheckingAlgoliaForRedirect(true);

          // FAST PATH: for product-classified queries the user usually typed a
          // specific product whose page already exists. A cheap, index-backed
          // exact lookup (PK on the derived id, ~ms) lets us redirect INSTANTLY
          // instead of first waiting on the fuzzy search RPC — which can take
          // seconds or hit its 3s timeout while the DB is under load. On a miss
          // (typos / near matches) we fall through to the fuzzy search below.
          {
            // Same helper the other entry points use — one implementation, so
            // a degraded lookup is handled identically everywhere.
            const outcome = await tryInstantExistingPageRedirect(userInput);
            if (outcome === 'redirected') return;
            if (outcome === 'degraded') return failSearchAsDegraded();
          }

          // First, check Algolia for existing matches
          try {
            const outcome = await searchPagesWithStatus({
              currentHref: pathname,
              searchTerm: userInput,
              locale,
              isDone: true,
              subregionPref: subregionFirstPref,
              patient: true,
            });

            // Unanswerable, not unmatched — do not fall through to creation.
            if (outcome.degraded) {
              failSearchAsDegraded();
              return;
            }
            const algolia = outcome.hits;
            untranslatedHitsRef.current = outcome.untranslated;
            // A one-shot product lookup names no products of a conversation.
            focusHitsRef.current = [];
            rankedHitsRef.current = outcome.hits;
            setShelfSubregionCode(outcome.subregion);

            if (algolia) {
              const algoliaResults = Array.isArray(algolia) ? algolia : [algolia];

              const exactMatches = algoliaResults.filter((hit) =>
                isAutoRedirectCandidate(hit, algoliaResults, userInput),
              );

              if (exactMatches.length > 0) {
                // Found exact match - redirect only if price tier matches
                let bestMatch = exactMatches.find(hit => hit.lang === locale.path);
                if (!bestMatch) {
                  bestMatch = exactMatches.find(hit => hit.lang?.split('-')[0] === locale.languageCode);
                }
                if (!bestMatch) {
                  bestMatch = exactMatches[0];
                }

                if (bestMatch) {
                  const redirectUrl = selectLocalizedUrl(bestMatch.href, locale);
                  if (redirectUrl) {
                    // Keep isCheckingAlgoliaForRedirect=true so the loading UI stays suppressed
                    // until navigation completes. The component will unmount during redirect anyway.
                    addToSearchHistory({
                      searchQuery: userInput,
                      href: redirectUrl,
                      locale: locale.path,
                      pg18: bestMatch.pg18 || false,
                    });
                    window.location.href = redirectUrl;
                    return;
                  }
                }
              }

              const bestCoverage = algoliaResults.reduce(
                (m, hit) => (typeof hit?.coverage === 'number' && hit.coverage > m ? hit.coverage : m),
                -1,
              );
              const shelfFloor =
                bestCoverage >= 0 ? Math.max(SHELF_ABSOLUTE_FLOOR, bestCoverage * SHELF_RELATIVE_RATIO) : 0;
              const displayMatches = !algoliaResults.some((hit) => passesDisplayGate(hit))
                ? algoliaResults
                    .filter(
                      (hit) =>
                        isNearExactSubjectMatch(userInput, hit.title) ||
                        passesNearMissRescue(hit, userInput),
                    )
                    .slice(0, 3)
                : [
                    ...algoliaResults.filter((hit) => passesShelfFloor(hit, shelfFloor)),
                    ...untranslatedHitsRef.current.filter((hit) => passesShelfFloor(hit, shelfFloor)),
                  ];

              // Show Algolia results to the user and let them choose.
              // This covers: no exact match found.
              // We do NOT start page creation yet — the user decides via "Create new search".
              if (displayMatches.length > 0 && !isBypassingAlgolia.current) {
                setIsCheckingAlgoliaForRedirect(false);
                setAlgoliaMatchs(displayMatches);
                setIsDoneFinal(true);
                // Store context so handleCreateNewSearch can trigger creation on demand
                pendingCreateNewSearchRef.current = {
                  userInput,
                  searchType: classification.searchType,
                };
                return;
              }
            }
          } catch (error) {
            // The fuzzy search threw. The exact-existence fast path above ran
            // first, but it returns a MISS on a degraded lookup too, so its
            // silence is not proof the page is absent. Treat this as degraded
            // rather than creating a page we cannot show is missing.
            console.warn('[search] product fast-path search failed:', error);
            failSearchAsDegraded();
            return;
          }

          // No existing page found - show loading animation and create page
          setIsCheckingAlgoliaForRedirect(false);
          setIsProductBranchLoading(true);
          setIsDoneFinal(true);
          const run = searchRunRef.current;
          // Product-type searches are price-neutral: no budget/premium tiers applied
          await executeSearchMapCreation(userInput, true, classification.searchType, '$$', 'default');
          if (run === searchRunRef.current) setIsProductBranchLoading(false);
          return;
        }

        // Otherwise, continue with Q&A for category/kit/software
        setAlgoliaMatchs(undefined);
        // Clear search input now that we're starting the actual search
        setSearchInput('');
        // isSearching already set when classification started

        // Use the product search chat hook
        const classifiedSearchBody = {
          locale,
          isDemo,
          productTitles,
          pageContext: title,
          shouldUsePageContext: classification.shouldUsePageContext,
          userRegion: chatUserRegion,
          searchType: classification.searchType, // 'kit' starts the conversation at kit scope
        };
        if (attachmentsRef.current.length > 0) {
          await sendMessageWithAttachments(userInput, attachmentsRef.current, classifiedSearchBody);
        } else {
          // Set the input first, then submit
          setInput(userInput);
          handleSubmit(undefined, { body: classifiedSearchBody });
        }
      } else {
        setIsSearching(false);

        // Clear search input so user can type new questions (same as product search)
        setSearchInput('');

        // Use the page question chat hook - append to start or continue conversation.
        // Attachments ride the message (Gemini is multimodal on this route;
        // links and notes are folded into text there).
        const pageQuestionAttachments = await resolveChatAttachments(attachmentsRef.current);
        await appendPageMessage({
          role: 'user',
          content: userInput
        }, {
          ...(pageQuestionAttachments.length > 0 ? { experimental_attachments: pageQuestionAttachments } : {}),
          body: {
            locale,
            productTitles,
            pageContext: title
          }
        });
        if (attachmentsRef.current.length > 0) clearAttachments({ sent: true });
      }
    } catch (error) {
      reportSearchFailure('classify-intent-error', {
        errorMessage: error instanceof Error ? error.message : String(error),
        userInput,
      });
      // Classification failed - clear the classifying intent flag
      setIsClassifyingIntent(false);
      // Fallback to product search on classification error
      setCurrentSearchIntent('product-search');
      setIsSearching(true);
      deferSearchHelpers(userInput);
      setInput(userInput);
      handleSubmit(undefined, {
        body: {
          locale,
          isDemo,
          productTitles,
          pageContext: title,
          shouldUsePageContext: false,
          userRegion: chatUserRegion,
          searchType: currentClassification?.searchType,
        }
      });
    } finally {
      setIsParallelExecuting(false);
      // Ensure classifying intent flag is cleared
      setIsClassifyingIntent(false);
    }
  };

  // Classification only (no side effects)
  // When searchTypeOnly is true, use simplified prompt (no page context)
  const classifySearchIntentOnly = async (userInput: string, cacheKey: string, searchTypeOnly = false) => {
    const requestBody = {
      userInput,
      locale,
      productTitles: searchTypeOnly ? undefined : productTitles,
      pageContext: searchTypeOnly ? undefined : title,
      viewingHint: searchTypeOnly ? undefined : viewingHint,
      searchTypeOnly,
    };

    const response = await fetch('/api/classify-intent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`Intent classification failed: ${response.status} ${response.statusText}`);
    }

    // An off-page comparison is a shopping question for the discussion, not a
    // page question (normalize-classified-intent.ts).
    const classification = normalizeClassifiedIntent(await response.json());

    // Cache the full classification result
    setIntentCache((prev) => new Map(prev.set(cacheKey, classification)));

    return classification;
  };

  // Handle direct product search (when intent is known)
  const handleDirectProductSearch = async (userInput: string) => {
    // The last turn's shelf goes, and so does its lookup if still streaming.
    shelfStreamRef.current?.controller.abort();
    shelfStreamRef.current = null;
    setAlgoliaMatchs(undefined);
    setIsSearching(true);

    // Clear search input for product searches so user can type new queries
    setSearchInput('');

    const directSearchBody = {
      locale,
      isDemo,
      productTitles,
      pageContext: title,
      shouldUsePageContext: currentClassification?.shouldUsePageContext ?? false,
      userRegion: chatUserRegion,
    };
    if (attachmentsRef.current.length > 0) {
      await sendMessageWithAttachments(userInput, attachmentsRef.current, directSearchBody);
    } else {
      // If we already have messages, use append to continue the conversation
      // Otherwise use handleSubmit to start fresh
      if (messages.length > 0) {
        await append({
          role: 'user',
          content: `[User's answer - may not directly address your question or indicate a search pivot]: ${userInput}`
        }, {
          body: directSearchBody
        });
      } else {
        setInput(userInput);
        handleSubmit(undefined, { body: directSearchBody });
      }
    }
  };

  // Handle direct page question (when intent is known)
  const handleDirectPageQuestion = async (userInput: string) => {
    track('page_question', { kind: 'ask' });
    setIsSearching(false);

    // Clear search input so user can type new questions (same as product search)
    setSearchInput('');

    // Always use append for page questions to maintain conversation context.
    // Attachments ride the message (links and notes are folded into text there).
    const pageAttachments = await resolveChatAttachments(attachmentsRef.current);
    await appendPageMessage({
      role: 'user',
      content: userInput
    }, {
      ...(pageAttachments.length > 0 ? { experimental_attachments: pageAttachments } : {}),
      body: {
        locale,
        productTitles,
        pageContext: title
      }
    });
    if (attachmentsRef.current.length > 0) clearAttachments({ sent: true });
  };

  /* -----------------------------------------------------------
   * Attachments (photo, links, notes)
   * --------------------------------------------------------- */
  const applyAttachments = (next: ComposerAttachment[]) => {
    attachmentsRef.current = next;
    setAttachmentsState(next);
  };

  /**
   * Empty the bar's attachments. After a send (`sent`) a link's preview keeps
   * loading: the conversation's card shows it. A reset or a removal aborts a
   * preview nobody waits for any more.
   */
  const clearAttachments = ({ sent }: { sent: boolean }) => {
    for (const item of attachmentsRef.current) {
      if (item.kind === 'link') releaseLinkPreview(item.url, { abort: !sent });
    }
    preparedImagesRef.current.clear();
    applyAttachments([]);
  };

  // A photo: one per message (a second replaces the first). Its downscale to
  // a data URL starts now so sending is instant; one that cannot be brought
  // under the size cap is dropped with an inline hint.
  const addImageAttachment = (file: File) => {
    setSearchRejectedMessage(null);
    const { items, replaced } = withImage(attachmentsRef.current, file);
    if (replaced) preparedImagesRef.current.delete(replaced.id);
    const added = items[items.length - 1];
    const prepared = prepareImageAttachment(file);
    preparedImagesRef.current.set(added.id, prepared);
    prepared.catch((error) => {
      if (preparedImagesRef.current.get(added.id) !== prepared) return; // replaced or sent
      preparedImagesRef.current.delete(added.id);
      applyAttachments(attachmentsRef.current.filter((item) => item.id !== added.id));
      if (error instanceof ImageTooLargeError) {
        setSearchRejectedMessage(getSearchUiContent(locale).imageTooLarge);
      }
    });
    applyAttachments(items);
  };

  // A link: normalized (another shop's tags dropped), its preview asked for at once.
  const addLinkAttachment = (raw: string): AddAttachmentResult => {
    const { items, result, added } = withLink(attachmentsRef.current, raw);
    if (result === 'limit') setSearchRejectedMessage(getSearchUiContent(locale).attachmentLimit);
    if (result !== 'added' || !added) return result;
    setSearchRejectedMessage(null);
    applyAttachments(items);
    holdLinkPreview(added.url);
    void fetchLinkPreview(added.url, {
      locale: locale.path,
      path: typeof window !== 'undefined' ? window.location.pathname : `/${locale.path}`,
      headers: searchHelperGate.headers(),
      onResponseHeaders: (headers) => searchHelperGate.remember(headers),
    });
    return result;
  };

  const addNoteAttachment = (text: string): AddAttachmentResult => {
    const { items, result } = withNote(attachmentsRef.current, text);
    if (result === 'limit') setSearchRejectedMessage(getSearchUiContent(locale).attachmentLimit);
    if (result !== 'added') return result;
    setSearchRejectedMessage(null);
    applyAttachments(items);
    return result;
  };

  const removeAttachment = (id: string) => {
    const item = attachmentsRef.current.find((a) => a.id === id);
    if (!item) return;
    if (item.kind === 'link') releaseLinkPreview(item.url);
    if (item.kind === 'image') preparedImagesRef.current.delete(item.id);
    applyAttachments(attachmentsRef.current.filter((a) => a.id !== id));
  };

  /** The AI SDK attachments for what is in the bar now (a failed photo is skipped). */
  const resolveChatAttachments = (items: readonly ComposerAttachment[]) =>
    toChatAttachments(items, async (image) => {
      try {
        return await (preparedImagesRef.current.get(image.id) ?? prepareImageAttachment(image.file));
      } catch {
        return null; // degrade to a send without the photo
      }
    });

  /**
   * Send a product-search turn with the bar's attachments riding the user
   * message itself (`experimental_attachments`): photos as images, links and
   * notes as small text data URLs the route folds into framed text. The model
   * sees them on this turn and on every later one, and the thread renders
   * them. A turn may be attachments alone (a pasted link). Mirrors the
   * text-only paths: fresh chats submit, ongoing chats append the wrapped
   * answer. The bar is cleared once they are sent.
   */
  const sendMessageWithAttachments = async (
    textMessage: string,
    items: readonly ComposerAttachment[],
    requestBody: Record<string, unknown>,
  ) => {
    const experimental_attachments = await resolveChatAttachments(items);
    const chatOptions = {
      ...(experimental_attachments.length > 0 ? { experimental_attachments } : {}),
      body: requestBody,
    };

    let pending: Promise<unknown> | undefined;
    if (messages.length > 0) {
      pending = append(
        {
          role: 'user',
          content: textMessage
            ? `[User's answer - may not directly address your question or indicate a search pivot]: ${textMessage}`
            : '',
        },
        chatOptions,
      );
    } else {
      setInput(textMessage);
      handleSubmit(undefined, { ...chatOptions, allowEmptySubmit: true });
    }

    // Clear the bar right away: the attachments now ride the user message and
    // show in the discussion thread instead.
    clearAttachments({ sent: true });
    if (pending) await pending;
  };

  // Trigger a new search based on current input with parallel execution
  // `textOverride` "" sends what is attached with no words (a pasted link).
  const handleNewSearch = async (textOverride?: string) => {
    const text = textOverride ?? input;
    const hasSomething = Boolean(text) || attachmentsRef.current.length > 0;
    if (!hasSomething || isLoading || isPageLoading || isParallelExecuting) {
      if (hasSomething && (isLoading || isPageLoading || isParallelExecuting)) {
        reportSearchFailure('parallel-executing-stuck', { userInput: text, errorMessage: `Blocked: isLoading=${isLoading} isPageLoading=${isPageLoading} isParallelExecuting=${isParallelExecuting}` });
      }
      return;
    }

    try {
      // Execute all requests in parallel and use the appropriate result
      await executeParallelSearch(text);

      setEnterPressed(false);
      inputRef.current?.blur();
    } catch (error) {
      console.error('Error in handleNewSearch:', error);
      // Fallback to product search with proper input
      setCurrentSearchIntent('product-search');
      setIsSearching(true);
      // Input is already set, just submit
      handleSubmit(undefined, {
        allowEmptySubmit: true,
        body: {
          locale,
          isDemo,
          productTitles,
          pageContext: title,
          shouldUsePageContext: false,
          searchType: currentClassification?.searchType,
        }
      });
    }
  };

  /**
   * Abandon this search because the database could not answer it, and show the
   * retry affordance. Deliberately does NOT fall through to page creation:
   * generating a page we cannot prove is missing is how duplicates get minted
   * and generation spend gets burned during an outage.
   */
  const failSearchAsDegraded = (
    cause: 'refused' | 'failed' = 'failed',
    // What failed: the AI turn (or a lookup made before any turn), or the page
    // lookup AFTER a turn that streamed fine. Decides what Retry repeats.
    stage: FailedSearchStage = 'turn',
    // A 429's window, when the limiter gave one.
    retryAfterSeconds: number | null = null,
  ): void => {
    failedStageRef.current = stage;
    setRetryNotBefore(retryAfterSeconds ? Date.now() + retryAfterSeconds * 1000 : null);
    failedOverTurnIdRef.current =
      [...messagesRef.current].reverse().find((m) => m.role === 'assistant')?.id ?? null;
    setSearchDegraded(true);
    // Two surfaces, because they cover different states. The PromptBanner
    // errorSlot (with its Retry button) only renders once a conversation
    // exists — the product fast-path can degrade before any turn has streamed,
    // and that case would otherwise fail silently. This message always renders
    // under the composer: `parseErrorRetry`, or `searchCouldNotRun` when the
    // gates refused the search (403/429). Both are translated into every locale.
    setSearchRejectedMessage(failedSearchMessage(getSearchUiContent(locale), cause));
    setIsCheckingAlgoliaForRedirect(false);
    setIsProductBranchLoading(false);
    setIsParallelExecuting(false);
    setIsSearching(false);
    setIsClassifyingIntent(false);
    setEnterPressed(false);
    // A lookup that failed after its turn streamed leaves the shopper's answer
    // where it belongs, in the conversation. Handing it back as well invited a
    // second send of words the model had already answered.
    if (stage === 'shelf') return;
    // Give the shopper their words back. Every failure path of a turn (a 403
    // or 429 from the gates, a 5xx, a timeout, a dropped request) used to leave
    // an empty composer, because submitting had cleared it. The composer text
    // (`searchInput`), never the chat `input`: setting `input` auto-runs a
    // search, which would re-submit the failed query.
    const words = lastUserInputRef.current;
    if (words) setSearchInput(words);
  };

  /** A later success takes down the failure banner an earlier attempt left up. */
  const clearSearchFailure = () => {
    failedStageRef.current = null;
    failedOfflineRef.current = false;
    setSearchDegraded(false);
    setSearchRejectedMessage(null);
    setRetryNotBefore(null);
  };

  const handleProductSearchRetry = () => {
    productSearchRetryCountRef.current = 0;
    setProductSearchParseError(false);
    // Repeat the step that failed, and only that step (lib/search/search-retry.ts).
    // The parse-error case has no failed stage: it streamed a turn the app
    // could not read, and regenerates it below as it always did.
    if (searchDegraded) {
      const plan = searchRetryPlan({
        stage: failedStageRef.current,
        lastMessageRole: messages[messages.length - 1]?.role,
      });
      clearSearchFailure();
      networkRetryUsedRef.current = false;
      if (plan === 'refetch-shelf') {
        // The turn on screen stands. Only its lookup is asked again: no AI
        // call, and no second copy of the shopper's answer.
        clearShelfRetry();
        lastFetchedQueryRef.current = null;
        setIsSearching(true);
        setShelfRetryTick((tick) => tick + 1);
        return;
      }
      if (plan === 'reload-turn') {
        // The unanswered message is still the conversation's last one, so the
        // composer copy handed back at failure is no longer needed.
        setSearchInput('');
        reloadTurn();
        return;
      }
      const lastInput = lastUserInputRef.current;
      if (lastInput) {
        void executeParallelSearch(lastInput);
        return;
      }
    }
    reload({
      body: {
        id: STREAM_SEARCH_CHAT_ID,
        modelId: GEMINI_FLASH_MODEL,
        locale,
        productTitles,
        pageContext: title,
        shouldUsePageContext: currentClassification?.shouldUsePageContext ?? false,
        userRegion: userExplicitlyChoseRegion && userRegion ? userRegion : undefined,
        searchType: currentClassification?.searchType,
      },
    });
  };
  retryHandlerRef.current = handleProductSearchRetry;

  /**
   * Retroactive edit from the scrollback history panel: replace an earlier
   * answer (or the original query when afterAssistantId is null), discard every
   * turn after it, and regenerate from there. Trailing-user-message reload() is
   * the same primitive handleRegionConfirm already relies on; setMessages()
   * updates the SDK's ref synchronously, so the back-to-back reload() sees the
   * truncated array without any sequencing delay.
   */
  const handleEditHistoryTurn = ({
    afterAssistantId,
    newAnswerText,
  }: {
    afterAssistantId: string | null;
    newAnswerText: string;
  }) => {
    // triggerRequest doesn't cancel a prior in-flight stream — without this an
    // old stream's onUpdate races the truncated array (same guard as the region
    // selector's Q2 interception above).
    stop();

    // The discarded tail may have concluded or errored. These don't derive from
    // `messages`, so they must be reset explicitly or stale results/error UI
    // lingers over the regenerating conversation.
    setProductSearchParseError(false);
    clearSearchFailure();
    networkRetryUsedRef.current = false;
    clearShelfRetry();
    setIsDoneFinal(false);
    setAlgoliaMatchs(undefined);
    setShelfSettledEmpty(false);
    lastProcessedInputRef.current = null;
    // The edit is now the last thing the shopper said: a failure of the turn
    // below must hand back these words, not the answer they replaced.
    lastUserInputRef.current = newAnswerText;

    // The AI accumulates these ACROSS turns and is told never to reset them, so
    // truncating `messages` does not unwind them: they would carry the discarded
    // branch's constraints into the regenerated one. `searchBrief.audience` is
    // the sharp edge — edit "backpack for women" to "for men" and page creation
    // still receives audience "women". Mirrors resetAllStates, which clears the
    // same three for the same reason.
    currentSearchScopeRef.current = 'single';
    confirmedElementsRef.current = [];
    searchBriefRef.current = null;

    // An edited turn keeps what was attached to it (a pasted link, a note, a
    // photo): only the words change. `parts` is rebuilt from the new words:
    // the AI SDK reads a user message's text from `parts` before `content`, so
    // a stale copy would send the model the words the shopper just replaced.
    if (afterAssistantId === null) {
      const original = messages[0];
      if (!original) return;
      setMessages([
        {
          ...original,
          id: generateId(),
          content: newAnswerText,
          parts: [{ type: 'text', text: newAnswerText }],
          createdAt: new Date(),
        },
      ]);
    } else {
      const idx = messages.findIndex((m) => m.id === afterAssistantId);
      if (idx === -1) return;
      const wrapped = `[User's answer - may not directly address your question or indicate a search pivot]: ${newAnswerText}`;
      const replaced = messages[idx + 1]?.role === 'user' ? messages[idx + 1] : undefined;
      setMessages([
        ...messages.slice(0, idx + 1),
        {
          id: generateId(),
          role: 'user',
          content: wrapped,
          parts: [{ type: 'text', text: wrapped }],
          createdAt: new Date(),
          ...(replaced?.experimental_attachments?.length
            ? { experimental_attachments: replaced.experimental_attachments }
            : {}),
        },
      ]);
    }

    setIsSearching(true);
    reload({
      body: {
        id: STREAM_SEARCH_CHAT_ID,
        modelId: GEMINI_FLASH_MODEL,
        locale,
        productTitles,
        pageContext: title,
        shouldUsePageContext: currentClassification?.shouldUsePageContext ?? false,
        userRegion: chatUserRegion,
        searchType: currentClassification?.searchType,
      },
    });
  };

  const endSearch = () => {
    setIsSearching(false);
    setIsDoneFinal(false);
    setIsDemo(false);
    setDemoType(null);
  };

  // Extract the common search map creation logic to avoid duplication
  const executeSearchMapCreation = async (
    searchQuery: string,
    forceBypass = false,
    preValidatedSearchType?: SearchType,
    priceRangeOverride?: PriceRange,
    priceRangeSourceOverride?: PriceRangeSource,
    budgetOverride?: BudgetSpec,
  ) => {
    if (!result && (!isDoneFinal || forceBypass)) {
      // Process creation of search map and update state.
      setIsDoneFinal(true);
      // Mark creation as in-flight so the stuck-loading watchdog defers to it.
      searchMapCreationInFlightRef.current = true;
      // Changed by Stop (or any reset) while the server works: its answer is dropped.
      const run = searchRunRef.current;

      try {
        // Kit conversations (classified upfront or escalated mid-conversation via the
        // AI's searchScope) hand off to the kit pipeline: decomposition → element
        // matching → streaming kit page. The rich query IS the searchQuery here.
        // Price tier applies to BOTH paths (normal search and kit handoff):
        // pages default to '$$', and an explicit '$'/'$$$' the shopper picked
        // shapes which products get generated. Computed here so the kit branch
        // below can pass it through instead of dropping it.
        const isProductType =
          currentSearchScopeRef.current !== 'kit' && preValidatedSearchType === 'product';
        const effectivePriceRange = isProductType
          ? '$$'
          : (priceRangeOverride ?? selectedPriceRange ?? '$$');
        const effectivePriceRangeSource = isProductType
          ? 'default'
          : (priceRangeSourceOverride ?? priceRangeSource);
        // Stated numeric budget rides category + kit paths. Product-type
        // searches are price-neutral — a leftover chip must not create
        // "iPhone 18 Pro Max budget".
        const effectiveBudget = isProductType
          ? undefined
          : (budgetOverride ?? toBudgetSpec(budgetRef.current));
        // Conversational brief, when this handoff came from a conversation. The
        // fast paths (a typed product query, a re-run from stored context) reset
        // the ref and correctly hand off without one.
        const effectiveBrief = searchBriefRef.current ?? undefined;

        const effectiveSearchType: SearchType | undefined =
          currentSearchScopeRef.current === 'kit' ? 'kit' : preValidatedSearchType;
        if (effectiveSearchType === 'kit') {
          const kitUrl = await startKitSearch({
            richQuery: searchQuery,
            locale,
            userRegion: userExplicitlyChoseRegion && userRegion ? userRegion : undefined,
            priceRange: effectivePriceRange,
            priceRangeSource: effectivePriceRangeSource,
            // Total-budget context persisted on the kit job for future allocation
            // work — kit children are still built tier-based in v1.
            budget: effectiveBudget,
            confirmedElements: confirmedElementsRef.current.length
              ? confirmedElementsRef.current
              : undefined,
            searchBrief: effectiveBrief,
            seedProduct: seedProduct ?? undefined,
          });
          if (run !== searchRunRef.current) return;
          if (kitUrl && kitUrl !== 'ERROR') {
            const persistedKitUrl = stripSearchIntentQuery(kitUrl);
            track('kit_created', {
              kit_href: persistedKitUrl.slice(0, 256),
              seeded: Boolean(seedProduct),
              elements_confirmed: confirmedElementsRef.current.length,
            });
            addToSearchHistory({
              searchQuery,
              href: persistedKitUrl,
              locale: locale.path,
              pg18: false,
            });
            finalizeTranscript(searchQuery, persistedKitUrl, snapshotShelfForTranscript());
            // Keep loading state visible until navigation completes (same as page creation)
            router.push(kitUrl);
            return;
          }
          cancelSearch();
          return;
        }

        const resultMap = await createSearchMap({
          searchTerm: searchQuery,
          locale,
          userRegion: userExplicitlyChoseRegion && userRegion ? userRegion : undefined,
          priceRange: effectivePriceRange,
          priceRangeSource: effectivePriceRangeSource,
          budget: effectiveBudget,
          searchBrief: effectiveBrief,
          preValidatedSearchType,
        });
        if (run !== searchRunRef.current) return;

        if (resultMap && resultMap !== 'ERROR') {
          // Check if the result is adult content
          if (resultMap.map.pg18) {
            const ageVerified = localStorage.getItem('age-verified') === 'true';
            if (!ageVerified) {
              // Show age verification modal and store result for later
              setIsAdultContent(true);
              setShowAgeVerification(true);
              setPendingAdultResult(resultMap);
              // Don't proceed with setting result until user verifies
              return;
            }
          }

          setIsProductBranchLoading(false); // Done loading (product branch)
          setResult(resultMap);
          setFinalResult(resultMap);
        } else {
          cancelSearch();
        }
      } finally {
        // A newer run owns the flag once this one was stopped.
        if (run === searchRunRef.current) searchMapCreationInFlightRef.current = false;
      }
    }
  };

  /* -----------------------------------------------------------
   * Effect: Stuck-loading watchdog (defense-in-depth)
   *
   * The loading animation (the search bar's LoadingStatusRow, plus LoadingExtras
   * for the ad/reassurance message) is shown whenever isDoneFinal === true.
   * It only resolves when it has Algolia results to render, a final `result` to redirect
   * to, or a page-creation redirect. If we ever enter isDoneFinal with NONE of those and
   * nothing actively in flight, the user would be stuck on the spinner forever.
   *
   * The explicit logic above is designed to never produce that state, but this watchdog
   * guarantees recovery regardless of how we got there: after a short grace period it
   * creates the page (or, as a last resort, cleanly resets the search).
   * --------------------------------------------------------- */
  const STUCK_LOADING_RECOVERY_MS = 6000;
  useEffect(() => {
    const hasMatches = !!algoliaMatchs && (Array.isArray(algoliaMatchs) ? algoliaMatchs.length > 0 : true);
    // A just-restored conclusion looks IDENTICAL to "stuck" for one or two effect
    // passes while its shelf snapshot/refetch catches up (see resumeChat and the
    // isRestoredTurn branch in the message-processing effect) — without this
    // exclusion the watchdog force-creates a duplicate page (or a second kit) a
    // few seconds after every restore. A restored turn stays excluded only until
    // the user continues the conversation, which appends a fresh assistant id
    // that isn't in `restoredTurnIds`.
    const lastMessage = messages[messages.length - 1];
    const isRestoredState = !!lastMessage && restoredTurnIds.has(lastMessage.id);
    const looksStuck =
      currentSearchIntent === 'product-search' &&
      isDoneFinal === true &&
      !hasMatches &&
      !result &&
      !isProductBranchLoading &&
      !isCheckingAlgoliaForRedirect &&
      !isLoading &&
      !isParallelExecuting &&
      !showAgeVerification &&
      !isRestoredState &&
      // A degraded search is FINISHED, not stuck: it deliberately stopped and
      // is showing a retry affordance. Without this the watchdog would
      // force-create the page 6s later and reopen the exact hole
      // failSearchAsDegraded exists to close.
      !searchDegraded &&
      messages.length > 0;

    if (!looksStuck) {
      // State is healthy (or moved on) — arm the watchdog again for the next episode.
      stuckRecoveryAttemptedRef.current = false;
      return;
    }

    if (stuckRecoveryAttemptedRef.current) return;

    const timer = setTimeout(async () => {
      // Re-read live refs at fire time so we never act on stale info or step on
      // legitimate in-flight work.
      if (searchMapCreationInFlightRef.current || isBypassingAlgolia.current) return;

      stuckRecoveryAttemptedRef.current = true;

      // Derive the best-known query to recover with.
      let recoveryQuery = aiGeneratedQuery;
      if (!recoveryQuery && messages.length > 0) {
        const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant' || m.role === 'system');
        recoveryQuery = lastAssistant ? parseAssistantMessage({ message: lastAssistant }).searchQuery ?? '' : '';
      }

      if (!recoveryQuery) {
        // Nothing we can safely build a page from — reset rather than hang.
        reportSearchFailure('product-search-stream-error', {
          errorMessage: 'stuck-loading-no-query',
          userInput: lastUserInputRef.current || undefined,
        });
        cancelSearch();
        return;
      }

      reportSearchFailure('product-search-stream-error', {
        errorMessage: 'stuck-loading-recovered',
        userInput: recoveryQuery,
      });
      // forceBypass=true so the isDoneFinal guard inside executeSearchMapCreation lets us proceed.
      await executeSearchMapCreation(recoveryQuery, true, currentClassification?.searchType);
    }, STUCK_LOADING_RECOVERY_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentSearchIntent,
    isDoneFinal,
    algoliaMatchs,
    result,
    isProductBranchLoading,
    isCheckingAlgoliaForRedirect,
    isLoading,
    isParallelExecuting,
    searchDegraded,
    showAgeVerification,
    messages,
    aiGeneratedQuery,
    restoredTurnIds,
  ]);

  const cancelSearchRetry = () => {
    stop();
    setIsDoneFinal(true);
    setIsDemo(false);
    setMessages([]);

    setSearchPropositions([]);
    stopSearchProposition();
    setEnterPressed(false);
  };

  // Handle switching from page-question to product-search
  const handleSwitchToProductSearch = () => {
    // Get the last user message as the search query
    const lastUserMessage = pageMessages.filter(m => m.role === 'user').pop();
    const userQuery = lastUserMessage?.content || searchInput;

    // Clear page messages and switch to product search mode
    setPageMessages([]);
    setCurrentSearchIntent('product-search');
    setIsSearching(true);
    setAlgoliaMatchs(undefined);
    setSearchInput('');

    // Start a new product search with the user's query
    setInput(userQuery);
    handleSubmit(undefined, {
      body: {
        locale,
        isDemo,
        productTitles,
        pageContext: title,
        shouldUsePageContext: false,
        userRegion: chatUserRegion,
      }
    });
  };

  // "Create a new page" from the live results shelf: the user bypasses the matched
  // pages and asks for a freshly built one. The product fast-path stored its context
  // in pendingCreateNewSearchRef (handled by handleCreateNewSearch); the Q&A path
  // derives the query from the transcript. Matches are cleared and
  // isProductBranchLoading keeps the creation theater visible until navigation.
  const handleCreatePageFromShelf = async () => {
    if (pendingCreateNewSearchRef.current) {
      return handleCreateNewSearch();
    }

    const lastMessage = messages[messages.length - 1];
    if (!lastMessage) return;
    const searchQuery = parseAssistantMessage({ message: lastMessage }).searchQuery;
    if (!searchQuery) return;

    // Prevent automatic execution in the message effect while we bypass
    isBypassingAlgolia.current = true;
    buildFromShelfRef.current = { matches: algoliaMatchs, pendingCreate: null };
    setAlgoliaMatchs(undefined);
    setResult(undefined);
    setFinalResult(undefined);
    setIsSearching(true);
    setIsProductBranchLoading(true);
    const run = searchRunRef.current;

    try {
      updateUrlWithQuery(searchQuery, locale);
      await executeSearchMapCreation(searchQuery, true); // Force bypass isDoneFinal check
    } catch (error) {
      console.error('❌ Error creating a page from the shelf:', error);
      if (run === searchRunRef.current) cancelSearch();
    } finally {
      // Stop already put the shelf back; a newer build owns these.
      if (run === searchRunRef.current) {
        isBypassingAlgolia.current = false;
        setIsProductBranchLoading(false);
      }
    }
  };

  // Cap + normalize the currently-shown shelf into the shape persisted on the
  // transcript, so a later Back-navigation can restore the shelf instantly
  // instead of waiting on a refetch. Undefined when nothing is currently
  // shown (e.g. the create-page/kit paths already cleared algoliaMatchs
  // before finalizing) — resume falls back to a live refetch in that case.
  const snapshotShelfForTranscript = (): AlgoliaHit[] | undefined => {
    if (!algoliaMatchs) return undefined;
    const hits = Array.isArray(algoliaMatchs) ? algoliaMatchs : [algoliaMatchs];
    return hits.length > 0 ? hits.slice(0, 5) : undefined;
  };

  // A shelf link is about to navigate: persist the search history entry and finalize
  // the chat transcript, mirroring what the auto-redirect exits do.
  const handleShelfNavigate = (url: string) => {
    const query = aiGeneratedQuery || input || searchInput;
    if (query) {
      addToSearchHistory({
        searchQuery: query,
        href: url,
        locale: locale.path,
        pg18: false,
      });
    }
    finalizeTranscript(query || undefined, url, snapshotShelfForTranscript());
  };

  // Called when the user clicks "Create new search" from the Algolia results view.
  // Clears the existing results and starts page creation. For product-type searches, always price-neutral.
  const handleCreateNewSearch = async () => {
    const pending = pendingCreateNewSearchRef.current;
    if (!pending) return;
    pendingCreateNewSearchRef.current = null;

    isBypassingAlgolia.current = true;
    buildFromShelfRef.current = { matches: algoliaMatchs, pendingCreate: pending };
    setAlgoliaMatchs(undefined);
    setResult(undefined);
    setFinalResult(undefined);
    setIsProductBranchLoading(true);
    setIsDoneFinal(true);
    const run = searchRunRef.current;

    try {
      updateUrlWithQuery(pending.userInput, locale);
      // Product-type searches are price-neutral: no budget/premium tiers applied
      const isProductType = pending.searchType === 'product';
      await executeSearchMapCreation(
        pending.userInput,
        true,
        pending.searchType,
        isProductType ? '$$' : undefined,
        isProductType ? 'default' : undefined,
      );
    } catch (error) {
      console.error('❌ Error in handleCreateNewSearch:', error);
      if (run === searchRunRef.current) cancelSearch();
    } finally {
      // Stop already put the shelf back; a newer build owns these.
      if (run === searchRunRef.current) {
        isBypassingAlgolia.current = false;
        setIsProductBranchLoading(false);
      }
    }
  };

  const waitForCompletion = async (url: string, searchQuery?: string) => {
    const run = searchRunRef.current;
    const searchParams = new URLSearchParams(window.location.search);
    const redirectHref = await waitForSearchCompletion({ locale, url, searchParams });
    if (run !== searchRunRef.current) return;

    // Only cancel search if there's an error (no redirect URL)
    // Keep loading state visible when redirecting - it will naturally clear on navigation
    if (!redirectHref) {
      cancelSearch();
      return;
    }

    // Save to search history
    if (searchQuery) {
      addToSearchHistory({
        searchQuery,
        href: redirectHref,
        locale: locale.path,
        pg18: isAdultContent, // Include current adult content state
      });
    }
    window.location.href = redirectHref;
  };

  /* -----------------------------------------------------------
   * Render
   * ----------------------------------------------------------- */
  const searchContent = getSearchUiContent(locale);

  // The shopper's admin-1 scope for the shelf: server-resolved canonical code
  // when a retrieval ran this conversation, stored-code fallback for shelves
  // restored from a transcript snapshot. Name follows the CURRENT locale, so a
  // language flip re-localizes the chip with everything else.
  const shelfScopeCode = resolveShelfScopeCode(shelfSubregionCode, subregionFirstPref?.code);
  const shelfSubregionScope: ShelfSubregionScope | null = shelfScopeCode
    ? {
        code: shelfScopeCode,
        name: getLocalizedRegionName(locale.regionCode, shelfScopeCode, locale.languageCode),
        priority: subregionFirstPref?.priority ?? DEFAULT_REGION_PRIORITY,
      }
    : null;

  return (
    <div ref={ref} className="contents">
      {/* Age Verification Modal - Show when adult content is detected and user hasn't verified */}
      {showAgeVerification && (
        <AgeVerification
          locale={locale}
          onVerified={() => {
            setShowAgeVerification(false);
            // After verification, resume the search with the pending result
            if (pendingAdultResult) {
              setResult(pendingAdultResult);
              setFinalResult(pendingAdultResult);
              setPendingAdultResult(undefined);
            }
          }}
        />
      )}

      {/* Background gradient — desktop-only second layer, see isDesktopViewport above. */}
      {(isDemo || isSearching) && !topMenu && !header && isDesktopViewport && <RadialGradientBackground fadeIn size="sm" />}
      <TextAreaSearchBar
        // enableVoiceSearchForTesting={enableVoiceSearchForTesting} // COMMENTED OUT: Voice assistant feature
        algoliaMatchs={algoliaMatchs}
        shelfSettledEmpty={shelfSettledEmpty}
        cancelSearch={cancelSearch}
        stopSearch={stopSearch}
        enterPressed={enterPressed}
        handleSearchPropositionSubmit={handleSearchPropositionSubmit}
        header={header}
        embedded={embedded}
        input={input}
        inputRef={inputRef}
        isDemo={isDemo}
        isDoneFinal={isDoneFinal}
        isLoading={isLoading || isPageLoading || isClassifyingIntent || isParallelExecuting}
        isSearching={isSearching}
        locale={locale}
        messages={currentSearchIntent === 'product-search' ? messages : pageMessages}
        result={result}
        searchInput={searchInput}
        seedProduct={seedProduct}
        revealConversationTick={revealConversationTick}
        askingAbout={viewingHint?.source === 'ask-button' ? viewingHint.title : undefined}
        aiGeneratedQuery={aiGeneratedQuery}
        browsingLabel={resolverBrowsing ? getSearchUiContent(locale).browsingStatus : ''}
        searchPropositions={searchPropositions}
        setEnterPressed={setEnterPressed}
        setFocusingOnTextArea={setFocusingOnTextArea}
        setInput={setInput}
        setIsDemo={setIsDemo}
        setDemoType={setDemoType}
        setIsSearching={setIsSearching}
        setMessages={currentSearchIntent === 'product-search' ? setMessages : setPageMessages}
        setSearchInput={setSearchInput}
        setSearchPropositionInput={setSearchPropositionInput}
        setSearchPropositions={setSearchPropositions}
        setShowNote={setShowNote}
        showNote={showNote}
        searchRejectedMessage={searchRejectedMessage}
        setSearchRejectedMessage={setSearchRejectedMessage}
        stopSearchProposition={stopSearchProposition}
        topMenu={topMenu}
        attachments={attachments}
        onAddImage={addImageAttachment}
        onAddLink={addLinkAttachment}
        onAddNote={addNoteAttachment}
        onRemoveAttachment={removeAttachment}
        onSubmitAttachmentsOnly={() => {
          setEnterPressed(true);
          setAttachmentSubmitTick((tick) => tick + 1);
        }}
        onCreatePageFromShelf={handleCreatePageFromShelf}
        onShelfNavigate={handleShelfNavigate}
        subregionScope={shelfSubregionScope}
        // New agent-related props
        currentSearchIntent={currentSearchIntent}
        isClassifyingIntent={isClassifyingIntent}
        setIsClassifyingIntent={setIsClassifyingIntent}
        isParallelExecuting={isParallelExecuting}
        productTitles={productTitles}
        pageId={pathname?.split('/').pop()}
        setCurrentSearchIntent={setCurrentSearchIntent}
        onSwitchToProductSearch={handleSwitchToProductSearch}
        compactMode={compactMode}
        // Region selector props
        showRegionSelector={showRegionSelector}
        onRegionConfirm={handleRegionConfirm}
        onRegionSkip={handleRegionSkip}
        onRegionFocusDismiss={handleRegionFocusDismiss}
        activeRegionLabel={
          regionSelectionCompleted && userRegion && userRegion !== 'All regions'
            ? getLocalizedRegionName(locale.regionCode, userRegion, locale.languageCode)
            : null
        }
        onReOpenRegionSelector={() => {
          setRegionSelectionCompleted(false);
          setShowRegionSelector(true);
          setNeedsRegionSelection(true);
          setIsSearching(false);
        }}
        // The budget: the chip shows the number the search heard and is its only control
        budget={currentClassification?.searchType === 'product' ? null : budget}
        onBudgetCommit={currentClassification?.searchType === 'product' ? undefined : handleBudgetCommit}
        // Product-type search: show loading-clean (not when redirecting)
        isProductBranchLoading={isProductBranchLoading}
        isCheckingAlgoliaForRedirect={isCheckingAlgoliaForRedirect}
        // A degraded search reuses the parse-error affordance: same user-facing
        // meaning ("something went wrong, try again"), same Retry button, which
        // handleProductSearchRetry routes to a full re-run for this case.
        // A degraded search raises the conversation's error banner only when a
        // conversation is on screen to hold it. Before the first turn (a
        // refused or failed first submit) there is none, so the message under
        // the composer must stay visible instead of being suppressed as a
        // duplicate of a banner that never rendered.
        productSearchParseError={productSearchParseError || (searchDegraded && messages.length > 0)}
        onProductSearchRetry={handleProductSearchRetry}
        retryNotBefore={retryNotBefore}
        // Tutorial mode props
        tutorialMode={tutorialMode}
        onSearchFocus={onSearchFocus}
        initialQuery={initialQuery}
        onStreamTimeout={() => reportSearchFailure('stream-never-started', { userInput: input })}
        onResumeChat={resumeChat}
        restoredTurnIds={restoredTurnIds}
        onEditHistoryTurn={handleEditHistoryTurn}
      />
    </div>
  );
});
